import { supabase } from '@/lib/supabase';
import { toLocalDateString } from '@/app/utils/localDate';
import { fetchInBatches } from '@/app/lib/chunkInQuery';
import {
  fetchCustomerLedgerSalesForRange,
  fetchCustomerReceivedPaymentsForRange,
  ledgerSalesRpcBranchId,
} from '@/app/services/customerLedgerApi';
import { formatRentalPaymentRef, isRcvReference } from '@/app/lib/rentalPaymentRef';
import {
  customerReceiptLedgerDescription,
  rentalPaymentLedgerDescription,
  resolvePartyLedgerReference,
} from '@/app/lib/partyLedgerReference';
import { filterLivePaymentsExcludingVoidedJournals } from '@/app/lib/paymentVoidVisibility';
import { isPartyDiscountJournalForContact } from '@/app/lib/partyLedgerLegacyJournalMatch';
import { appendRentalPaymentMergeItems } from '@/app/lib/rentalPenaltyLedgerLines';
import {
  mergeAttachmentLists,
  normalizeAttachmentList,
  receiptUrlToAttachments,
  type TransactionAttachment,
} from '@/app/utils/transactionAttachments';
import { resolveStockMovementDisplayAmount } from '@/app/utils/stockMovementValuation';
import { canUpdateViaManualJournalEditor } from '@/app/lib/journalEntryEditPolicy';
import {
  filterApRowsByPartyRole,
  recalcApLiabilityRunningBalances,
  splitRoleFilteredApRowsByPeriod,
} from '@/app/lib/importFxPartyLedgerRoleFilter';
import { JOURNAL_ENTRIES_LIST_SELECT } from '@/app/lib/journalEntriesListSelect';

export { JOURNAL_ENTRIES_LIST_SELECT } from '@/app/lib/journalEntriesListSelect';

export interface JournalEntry {
  id?: string;
  company_id: string;
  branch_id?: string;
  entry_no?: string;
  /** Mirrored operational ref (e.g. JV- / FT-); same as entry_no when set by callers. */
  document_no?: string | null;
  entry_date: string;
  description?: string;
  reference_type?: string;
  reference_id?: string;
  payment_id?: string;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
  /** Optional attachments (same as payments: [{ url, name }]). Requires journal_entries.attachments column. */
  attachments?: { url: string; name: string }[] | null;
  /** PF-14.5B: Logical action fingerprint for duplicate prevention; same fingerprint must not create multiple active JEs. */
  action_fingerprint?: string | null;
  /** Phase 4: Chain key for primary + PF-14 rows (often payment UUID for receipts). */
  economic_event_id?: string | null;
}

export interface JournalEntryLine {
  id?: string;
  journal_entry_id?: string;
  account_id: string;
  debit: number;
  credit: number;
  description?: string;
  created_at?: string;
}

export interface JournalEntryWithLines extends JournalEntry {
  lines: JournalEntryLine[];
}

export interface AccountLedgerEntry {
  date: string;
  /** ISO datetime for sort/display (from journal_entries.created_at) */
  created_at?: string;
  reference_number: string;
  entry_no?: string | null; // Actual entry_no from database (for lookup)
  description: string;
  debit: number;
  credit: number;
  running_balance: number;
  source_module: string;
  created_by?: string;
  journal_entry_id: string;
  /** Header reference_type from journal_entries (presentation / routing). */
  je_reference_type?: string | null;
  /** PF-14 / Phase 4 fingerprint on journal_entries. */
  je_action_fingerprint?: string | null;
  /** journal_entry_lines.id for control-1100 repair pairing. */
  journal_line_id?: string | null;
  /** journal_entries.reference_id (source document / source JE for gl_correction). */
  je_reference_id?: string | null;
  payment_id?: string;
  sale_id?: string;
  rental_id?: string;
  branch_id?: string;
  branch_name?: string;
  account_name?: string; // Payment Account name (from account_id)
  /** Other account(s) in this double-entry (e.g. "Bank ABC" when viewing Cash ledger) */
  counter_account?: string;
  notes?: string; // User notes/narration (separate from description)
  document_type?: string; // Document Type (Sale, Payment, etc.)
  /** GL account code on the viewed line (e.g. 2000, 2010) for multi-account party views */
  gl_account_code?: string;
  /** correction_reversal rows; UI can style distinctly */
  ledger_kind?: 'standard' | 'reversal';
  /** journal_entries.economic_event_id when present */
  economic_event_id?: string | null;
  /** Linked sale.status when JE references a sale (sale / sale_reversal / sale_return). */
  linked_sale_status?: string | null;
  /** payments.voided_at when row is payment-linked (voided payment trail). */
  payment_voided_at?: string | null;
}

/**
 * Some postings create two AR credit lines for the same receipt (e.g. `reference_type=payment` plus
 * a `sale` JE with `payment_id` set). That double-counts running balance and statements — keep one line per payment.
 */
/** Map journal_entries.reference_type to Account Statements “Source module” filter (lowercased compare in UI). */
function glStatementSourceModuleFromReferenceType(referenceType: string | undefined | null): string {
  const t = String(referenceType || '').toLowerCase().trim();
  if (['sale', 'sale_return', 'sale_adjustment', 'sale_extra_expense', 'sale_reversal'].includes(t)) return 'Sales';
  if (['purchase', 'purchase_return', 'purchase_adjustment', 'purchase_reversal'].includes(t)) return 'Purchase';
  if (t === 'rental') return 'Rental';
  if (['worker_payment', 'worker_advance_settlement', 'studio_production_stage', 'studio_production_stage_reversal'].includes(t))
    return 'Studio';
  return 'Accounting';
}

function glStatementDocumentTypeFromReferenceType(
  referenceType: string | undefined | null,
  fallbackLabel: string
): string {
  const t = String(referenceType || '').toLowerCase().trim();
  if (t === 'sale') return 'Sale';
  if (t === 'sale_return') return 'Sale return';
  if (t === 'sale_reversal') return 'Sale reversal';
  if (t === 'sale_adjustment' || t === 'sale_extra_expense') return 'Sale adjustment';
  if (t === 'payment' || t === 'payment_adjustment') return 'Payment';
  if (t === 'manual_receipt') return 'Manual receipt';
  if (t === 'manual_payment') return 'Manual payment';
  if (t.startsWith('opening_balance')) return 'Opening balance';
  if (t === 'correction_reversal') return 'Reversal';
  return fallbackLabel;
}

function extractBankTraceId(notes?: string | null): string {
  const raw = String(notes || '').trim();
  if (!raw) return '';
  const match = raw.match(/bank\s*trace\s*id\s*:\s*([^|]+)/i);
  return match?.[1]?.trim() || '';
}

function journalLineNormalizedPaymentId(line: { journal_entry?: any; credit?: number }): string | null {
  const entry = line.journal_entry;
  if (!entry) return null;
  const credit = Number(line.credit || 0);
  if (credit <= 0) return null;
  if (entry.payment_id) return String(entry.payment_id);
  if (entry.reference_type === 'payment' && entry.reference_id) return String(entry.reference_id);
  return null;
}

/**
 * Customer receipts: Dr Cash/Bank, Cr AR. Supplier payments: Dr AP, Cr Bank.
 * Remap the aggregated debit line to payment_account_id only when that debit is a liquidity (cash/bank/wallet) line
 * left stale after a payment-method edit. If we remap AP or expense debits, the modal shows the same bank on both sides.
 */
function isLiquidityDebitForPaymentRemap(account: { code?: string; name?: string; type?: string } | null | undefined): boolean {
  if (!account) return false;
  const code = String(account.code ?? '').trim();
  const name = (account.name ?? '').toLowerCase();
  const type = (account.type ?? '').toLowerCase();
  if (type.includes('liability') || type.includes('payable')) return false;
  if (type.includes('expense') || type.includes('cost')) return false;
  if (name.includes('payable') && !name.includes('receivable')) return false;
  if (name.includes('receivable') && (name.includes('account') || name.includes('customer'))) return false;
  if (type.includes('cash') || type.includes('bank') || type.includes('wallet')) return true;
  if (
    name.includes('cash') ||
    name.includes('bank') ||
    name.includes('wallet') ||
    name.includes('easypaisa') ||
    name.includes('jazzcash')
  )
    return true;
  const codeNum = parseInt(code, 10);
  if (!Number.isNaN(codeNum) && codeNum >= 1000 && codeNum <= 1099) return true;
  return false;
}

function dedupeCustomerArPaymentCreditLines(sortedLines: any[]): any[] {
  const byPayment = new Map<string, any[]>();
  for (const line of sortedLines) {
    const pid = journalLineNormalizedPaymentId(line);
    if (!pid) continue;
    const g = byPayment.get(pid) || [];
    g.push(line);
    byPayment.set(pid, g);
  }
  const skip = new Set<any>();
  for (const [, lines] of byPayment) {
    if (lines.length <= 1) continue;
    const preferred =
      lines.find((l: any) => l.journal_entry?.reference_type === 'payment') ||
      lines.find((l: any) => Boolean(l.journal_entry?.payment_id)) ||
      lines[0];
    for (const l of lines) {
      if (l !== preferred) skip.add(l);
    }
  }
  return sortedLines.filter((l) => !skip.has(l));
}

/**
 * Whether an AR journal line belongs to this customer (before correction_reversal handling).
 * Used by getCustomerLedger; reversal rows are included separately when they reverse such a line.
 */
/**
 * Party AR line → customer statement matcher.
 *
 * LOCK (import / Account Statements gap): Do **not** widen this to accept generic
 * `journal` / null / bank-transfer headers solely because the debit/credit hits an
 * AR leaf or `accounts.linked_contact_id`. That would mix unrelated COA history into
 * party statements. Imported journals without sale/payment/rental/opening links belong
 * on **Type = Account** until repaired (`opening_balance_contact_ar` + contact id, or
 * real sale/payment rows). Future POS sales/payments already match here and show under
 * Type = Customer. See docs/accounting/CUSTOMER_STATEMENT_VS_ACCOUNT_IMPORT_GAP.md.
 */
function arJournalLineMatchesCustomer(
  line: any,
  customerId: string,
  salesMap: Map<string, any>,
  paymentIds: string[],
  paymentDetailsMap: Map<string, any>,
  customerSaleReturnIds: Set<string>,
  /** Rental booking ids for this party — include AR journal lines with reference_type rental when reference_id matches. */
  rentalIdsForParty: Set<string>
): boolean {
  const entry = line.journal_entry;
  if (!entry) return false;

  const desc = entry.description?.toLowerCase() || '';
  if (desc.includes('commission')) return false;

  const refTypeNorm = String(entry.reference_type || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_');
  /** Sale return settlement (Dr revenue / Cr AR or cash) — must match party or ledger omits credits and receivable stays wrong. */
  if (refTypeNorm === 'sale_return' && entry.reference_id) {
    return customerSaleReturnIds.has(String(entry.reference_id));
  }

  if ((entry.reference_type === 'sale' || entry.reference_type === 'sale_adjustment' || entry.reference_type === 'sale_reversal') && entry.reference_id) {
    const sale = salesMap.get(entry.reference_id);
    if (sale && sale.customer_id === customerId) return true;
  }

  if (entry.reference_type === 'payment' && entry.reference_id) {
    if (paymentIds.includes(entry.reference_id)) return true;
    const payment = paymentDetailsMap.get(entry.reference_id);
    if (payment && payment.reference_id) {
      const sale = salesMap.get(payment.reference_id);
      if (sale && sale.customer_id === customerId) return true;
    }
  }

  if (entry.reference_type === 'sale' && entry.payment_id) {
    if (paymentIds.includes(entry.payment_id)) return true;
    if (entry.reference_id) {
      const sale = salesMap.get(entry.reference_id);
      if (sale && sale.customer_id === customerId) return true;
    }
  }

  if (entry.reference_type === 'manual_receipt' && entry.payment_id && paymentIds.includes(entry.payment_id)) {
    return true;
  }
  if (entry.reference_type === 'manual_receipt' && entry.reference_id && String(entry.reference_id) === String(customerId)) {
    return true;
  }
  if (
    entry.reference_type === 'opening_balance_contact_ar' &&
    entry.reference_id &&
    String(entry.reference_id) === String(customerId)
  ) {
    return true;
  }
  if (entry.reference_type === 'rental' && entry.reference_id) {
    return rentalIdsForParty.has(String(entry.reference_id));
  }
  if (isPartyDiscountJournalForContact(entry, customerId)) {
    return true;
  }
  return false;
}

/** Build set of sale_return row ids that belong to this customer (by customer_id or by original sale in party sales). */
async function buildCustomerSaleReturnIdSet(
  companyId: string,
  customerId: string,
  salesMap: Map<string, any>
): Promise<Set<string>> {
  const ids = new Set<string>();
  const { data: rows, error } = await supabase
    .from('sale_returns')
    .select('id, customer_id, original_sale_id, status')
    .eq('company_id', companyId)
    .neq('status', 'void');
  if (error) {
    if (import.meta.env?.DEV) console.warn('[getCustomerLedger] sale_returns fetch:', error.message);
    return ids;
  }
  for (const sr of rows || []) {
    const id = sr.id != null ? String(sr.id) : '';
    if (!id) continue;
    if (String(sr.customer_id || '') === String(customerId)) {
      ids.add(id);
      continue;
    }
    const orig = sr.original_sale_id != null ? String(sr.original_sale_id) : '';
    if (orig && salesMap.has(orig)) {
      ids.add(id);
    }
  }
  return ids;
}

/** All account ids from AP control (code 2000) through active descendants — matches GL RPC ap_subtree. */
function collectDescendantAccountIds(
  rows: { id: string; parent_id?: string | null }[],
  rootId: string
): string[] {
  const children = new Map<string, string[]>();
  for (const r of rows) {
    const p = r.parent_id != null && String(r.parent_id).trim() !== '' ? String(r.parent_id) : '';
    if (!children.has(p)) children.set(p, []);
    children.get(p)!.push(String(r.id));
  }
  const out: string[] = [];
  const seen = new Set<string>();
  const stack = [String(rootId)];
  while (stack.length) {
    const id = stack.pop()!;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    const ch = children.get(id) || [];
    for (const c of ch) stack.push(c);
  }
  return out;
}

function supplierApJournalLineMatchesSupplier(
  line: any,
  supplierId: string,
  supplierPurchaseIds: Set<string>,
  supplierPaymentIds: Set<string>
): boolean {
  const entry = line.journal_entry;
  if (!entry) return false;
  const rt = String(entry.reference_type || '').toLowerCase();
  const rid = entry.reference_id;
  if (
    ['purchase', 'purchase_return', 'purchase_adjustment', 'purchase_reversal'].includes(rt) &&
    rid &&
    supplierPurchaseIds.has(rid)
  ) {
    return true;
  }
  if (entry.payment_id && supplierPaymentIds.has(entry.payment_id)) return true;
  if (rt === 'payment' && rid && supplierPaymentIds.has(rid)) return true;
  if (rt === 'on_account' && rid && String(rid) === String(supplierId)) return true;
  if (rt === 'manual_payment' && rid && String(rid) === String(supplierId)) return true;
  if (rt === 'opening_balance_contact_ap' && rid && String(rid) === String(supplierId)) return true;
  if (isPartyDiscountJournalForContact(entry, supplierId)) return true;
  return false;
}

function workerGlLineMatchesWorker(
  line: any,
  workerId: string,
  workerPaymentIds: Set<string>,
  workerStageIds: Set<string>
): boolean {
  const entry = line.journal_entry;
  if (!entry) return false;
  const rt = String(entry.reference_type || '').toLowerCase();
  const rid = entry.reference_id;
  if ((rt === 'worker_payment' || rt === 'worker_advance_settlement') && rid && String(rid) === String(workerId)) {
    return true;
  }
  if (rt === 'opening_balance_contact_worker' && rid && String(rid) === String(workerId)) {
    return true;
  }
  if (rt === 'studio_production_stage' && rid && workerStageIds.has(String(rid))) {
    return true;
  }
  if (entry.payment_id && workerPaymentIds.has(entry.payment_id)) return true;
  return false;
}

/** Fallback if DB RPC `get_supplier_ap_gl_ledger_for_contact` is unavailable — client-side party filter (can miss payment graph). */
async function fetchSupplierApGlJournalLedgerLegacy(
  supplierId: string,
  companyId: string,
  branchId?: string,
  startDate?: string,
  endDate?: string
): Promise<AccountLedgerEntry[]> {
  const { data: companyAccounts } = await supabase
    .from('accounts')
    .select('id, code, name, parent_id, linked_contact_id')
    .eq('company_id', companyId)
    .eq('is_active', true);

  const rows = (companyAccounts || []) as { id: string; code?: string; name?: string; parent_id?: string | null }[];
  const apRoot = rows.find((a) => String(a.code ?? '').trim() === '2000');
  let apAccountIds: string[];
  if (apRoot?.id) {
    apAccountIds = collectDescendantAccountIds(rows, String(apRoot.id));
  } else {
    const apAccounts = rows.filter(
      (a) =>
        String(a.code ?? '').trim() !== '1100' &&
        (String(a.code ?? '').trim() === '2000' ||
          String(a.name ?? '')
            .toLowerCase()
            .includes('accounts payable'))
    );
    if (apAccounts.length === 0) return [];
    apAccountIds = apAccounts.map((a) => a.id);
  }
  const apAccountIdSet = new Set(apAccountIds);

  const { data: lines, error } = await supabase
    .from('journal_entry_lines')
    .select(
      `
          *,
          account:accounts(id, name, code, linked_contact_id),
          journal_entry:journal_entries(
            id, entry_no, entry_date, description, reference_type, reference_id, payment_id,
            branch_id, created_by, created_at, is_void,
            branch:branches(id, name, code)
          )
        `
    )
    .in('account_id', apAccountIds)
    .order('created_at', { ascending: true });

  if (error) console.error('[ACCOUNTING SERVICE] getSupplierApGlJournalLedger lines error:', error);
  const linesToUse = (lines || []).filter((l: any) => (l.journal_entry && (l.journal_entry as any).is_void) !== true);

  const { data: purchases } = await supabase
    .from('purchases')
    .select('id')
    .eq('company_id', companyId)
    .eq('supplier_id', supplierId);
  const supplierPurchaseIds = new Set((purchases || []).map((p: { id: string }) => p.id));

  const { data: payRows } = await supabase
    .from('payments')
    .select('id, contact_id, reference_type, reference_id')
    .eq('company_id', companyId);
  const supplierPaymentIds = new Set<string>();
  (payRows || []).forEach((p: any) => {
    if (p.contact_id === supplierId) supplierPaymentIds.add(p.id);
    if (String(p.reference_type).toLowerCase() === 'purchase' && p.reference_id && supplierPurchaseIds.has(p.reference_id)) {
      supplierPaymentIds.add(p.id);
    }
  });

  const supplierLines = linesToUse.filter((line: any) => {
    if (!apAccountIdSet.has(String(line.account_id))) return false;
    const acc = line.account;
    if (acc?.linked_contact_id != null && String(acc.linked_contact_id) === String(supplierId)) {
      return true;
    }
    const entry = line.journal_entry;
    if (!entry) return false;
    const rt = String(entry.reference_type || '').toLowerCase();
    if (rt === 'correction_reversal' && entry.reference_id) {
      const origId = String(entry.reference_id);
      return linesToUse.some((ol: any) => {
        const oe = ol.journal_entry;
        if (!oe || String(oe.id) !== origId) return false;
        return supplierApJournalLineMatchesSupplier(ol, supplierId, supplierPurchaseIds, supplierPaymentIds);
      });
    }
    return supplierApJournalLineMatchesSupplier(line, supplierId, supplierPurchaseIds, supplierPaymentIds);
  });

  const branchFiltered = branchId
    ? supplierLines.filter((line: any) => {
        const bid = line.journal_entry?.branch_id;
        return bid == null || bid === '' || bid === branchId;
      })
    : supplierLines;

  let openingBalance = 0;
  if (startDate) {
    branchFiltered.forEach((line: any) => {
      const entry = line.journal_entry;
      if (!entry || entry.entry_date >= startDate) return;
      openingBalance += (line.credit || 0) - (line.debit || 0);
    });
  }

  const rangeLines = branchFiltered.filter((line: any) => {
    const entry = line.journal_entry;
    if (!entry) return false;
    const entryDate = entry.entry_date;
    if (startDate && entryDate < startDate) return false;
    if (endDate && entryDate > endDate) return false;
    return true;
  });

  rangeLines.sort((a: any, b: any) => {
    const dateA = String(a.journal_entry?.entry_date || '');
    const dateB = String(b.journal_entry?.entry_date || '');
    if (dateA !== dateB) return dateA.localeCompare(dateB);
    const createdA = String(a.journal_entry?.created_at || a.created_at || '');
    const createdB = String(b.journal_entry?.created_at || b.created_at || '');
    return createdA.localeCompare(createdB);
  });

  let runningBalance = openingBalance;
    const ledgerEntriesFromRange: AccountLedgerEntry[] = rangeLines.map((line: any) => {
    const entry = line.journal_entry;
    const debit = line.debit || 0;
    const credit = line.credit || 0;
    runningBalance += credit - debit;
    const code = line.account?.code != null ? String(line.account.code) : '';
    const rt = String(entry.reference_type || '');
    const effPay =
      entry.payment_id ||
      (rt.toLowerCase() === 'payment_adjustment' && entry.reference_id
        ? String(entry.reference_id)
        : undefined);
    return {
      date: entry.entry_date,
      created_at: entry.created_at,
      reference_number: entry.entry_no || entry.id?.slice(0, 8) || '—',
      entry_no: entry.entry_no,
      description: mergeLedgerEntryDescriptionForStatement(entry.reference_type, entry.description, line.description),
      debit,
      credit,
      running_balance: runningBalance,
      source_module: 'Accounting',
      journal_entry_id: entry.id,
      payment_id: effPay,
      je_reference_type: rt || undefined,
      branch_id: entry.branch_id,
      branch_name: entry.branch?.name,
      account_name: line.account?.name || '',
      gl_account_code: code,
      document_type: 'AP Journal',
    };
  });

  const ledgerEntries: AccountLedgerEntry[] = startDate
    ? [
        {
          date: startDate,
          reference_number: '—',
          entry_no: undefined,
          description: 'Opening Balance (AP GL)',
          debit: 0,
          credit: 0,
          running_balance: openingBalance,
          source_module: 'Accounting',
          journal_entry_id: '',
          document_type: 'Opening Balance',
          account_name: '',
        },
        ...ledgerEntriesFromRange,
      ]
    : ledgerEntriesFromRange;

  return ledgerEntries;
}

/**
 * PostgREST `.maybeSingle()` fails when multiple rows match (e.g. duplicate `entry_no` or duplicate primary `payment_id`).
 * For entry-no / payment resolution, prefer a **non-void** row, then **oldest** `created_at` (matches duplicate-repair “keep earliest”).
 */
function pickPreferredJournalEntryRow<T extends { is_void?: boolean | null; created_at?: string | null; id: string }>(
  rows: T[] | null | undefined
): T | null {
  if (!rows?.length) return null;
  const nonVoid = rows.filter((r) => r.is_void !== true);
  const pool = nonVoid.length ? nonVoid : rows;
  return [...pool].sort((a, b) => {
    const ta = new Date(a.created_at || 0).getTime();
    const tb = new Date(b.created_at || 0).getTime();
    if (ta !== tb) return ta - tb;
    return String(a.id).localeCompare(String(b.id));
  })[0];
}

/** Sale path: prefer newest non-void when several sale JEs exist for the same invoice. */
function pickLatestJournalEntryRow<T extends { is_void?: boolean | null; created_at?: string | null; id: string }>(
  rows: T[] | null | undefined
): T | null {
  if (!rows?.length) return null;
  const nonVoid = rows.filter((r) => r.is_void !== true);
  const pool = nonVoid.length ? nonVoid : rows;
  return [...pool].sort((a, b) => {
    const ta = new Date(a.created_at || 0).getTime();
    const tb = new Date(b.created_at || 0).getTime();
    if (tb !== ta) return tb - ta;
    return String(b.id).localeCompare(String(a.id));
  })[0];
}

/**
 * Statement list shows one description per line; purchase document JEs set a generic header on the entry
 * ("Purchase PUR-… from …") while charge-specific text lives on journal lines (freight (purchase), Payable - cargo, …).
 * Append the line narration when it encodes an extra charge / discount leg so Account Statements read clearly.
 */
function mergeLedgerEntryDescriptionForStatement(
  referenceType: string | null | undefined,
  entryDescription: string | null | undefined,
  lineDescription: string | null | undefined
): string {
  const ed = String(entryDescription || '').trim();
  const ld = String(lineDescription || '').trim();
  const rt = String(referenceType || '').toLowerCase();

  if (!ed && !ld) return '—';
  if (!ld) return ed || '—';
  if (!ed) return ld;

  const edL = ed.toLowerCase();
  const ldL = ld.toLowerCase();
  if (ldL === edL || edL.includes(ldL)) return ed;

  const append = () => `${ed} — ${ld}`;

  if (rt === 'purchase' || rt === 'purchase_adjustment') {
    const isChargeOrDiscountLine =
      /\((purchase|adj)\)\s*$/i.test(ld) ||
      /^payable\s*[-–]\s*\S+/i.test(ld) ||
      /\bpurchase\s+discount\b/i.test(ld) ||
      /^discount\s+received\b/i.test(ld) ||
      (rt === 'purchase_adjustment' &&
        (/\bfreight\/extra\b/i.test(ld) || /\bap\b.*\bfreight\b/i.test(ld) || /\bfreight\b.*\brs\b/i.test(ld)));
    if (isChargeOrDiscountLine) return append();
  }
  if (rt === 'purchase_reversal') {
    if (/^reversal:\s*/i.test(ld) || /\((purchase|adj)\)\s*$/i.test(ld) || /^payable\s*[-–]\s*\S+/i.test(ld)) {
      return append();
    }
  }

  return ed;
}

export const accountingService = {
  // Get all journal entries with lines
  // CRITICAL: Filter out entries for deleted purchases/sales
  async getAllEntries(
    companyId: string,
    branchId?: string,
    startDate?: string | Date,
    endDate?: string | Date,
    opts?: { limit?: number; offset?: number; mode?: 'list' | 'full' }
  ): Promise<any[] | { data: any[]; total: number }> {
    try {
      // Normalize dates to ISO YYYY-MM-DD so PostgREST accepts them (Date objects get serialized incorrectly otherwise).
      const startStr = startDate == null ? undefined : typeof startDate === 'string' ? startDate.slice(0, 10) : startDate.toISOString().slice(0, 10);
      const endStr = endDate == null ? undefined : typeof endDate === 'string' ? endDate.slice(0, 10) : endDate.toISOString().slice(0, 10);
      const limit = opts?.limit ?? 100;
      const offset = opts?.offset ?? 0;
      const listMode = opts?.mode === 'list';

      // List mode: explicit lean headers only (no `*`, no nested lines) — avoids 57014 under All Branches.
      // Full mode keeps nested select for detail/enrichment callers.
      const selectClause = listMode
        ? JOURNAL_ENTRIES_LIST_SELECT
        : `
          *,
          lines:journal_entry_lines(
            id,
            journal_entry_id,
            account_id,
            debit,
            credit,
            description,
            account:accounts(name, code, type)
          )
        `;

      // SOURCE LOCK (Phase 1): journal_entries (+ lines only in full mode) for GL.
      // List mode: when a date window is set, take a cheap exact count (same filters, head-only).
      // Unbounded From-start without dates still uses page-size hasMore estimate to avoid 57014.
      let listExactTotal: number | null = null;
      if (listMode && opts && (startStr || endStr)) {
        let countQuery = supabase
          .from('journal_entries')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .or('is_void.is.null,is_void.eq.false');
        if (branchId) {
          countQuery = countQuery.or(`branch_id.eq.${branchId},branch_id.is.null`);
        }
        if (startStr) countQuery = countQuery.gte('entry_date', startStr);
        if (endStr) countQuery = countQuery.lte('entry_date', endStr);
        const { count: exactCount, error: countErr } = await countQuery;
        if (!countErr && typeof exactCount === 'number') {
          listExactTotal = exactCount;
        }
      }

      let query = supabase
        .from('journal_entries')
        .select(selectClause, opts && !listMode ? { count: 'exact' } : undefined)
        .eq('company_id', companyId)
        .order('entry_date', { ascending: false })
        .order('created_at', { ascending: false });

      // Include company-wide JEs (NULL branch) when a branch is selected — matches TB / party GL readers.
      if (branchId) {
        query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
      }

      if (startStr) {
        query = query.gte('entry_date', startStr);
      }

      if (endStr) {
        query = query.lte('entry_date', endStr);
      }

      if (opts) {
        query = query.range(offset, offset + limit - 1);
      }

      const { data, error, count } = await query;
      
      // Handle missing table gracefully
      if (error && (error.code === 'PGRST205' || error.message?.includes('does not exist'))) {
        console.warn('[ACCOUNTING SERVICE] journal_entries table does not exist, returning empty array');
        return opts ? { data: [], total: 0 } : [];
      }
      
      if (error) {
        console.error('[ACCOUNTING SERVICE] Error fetching journal entries:', error);
        if (opts) throw error;
        return [];
      }

      if (!data || data.length === 0) {
        return opts ? { data: [], total: listExactTotal ?? count ?? 0 } : [];
      }

      // PF-14.4: Exclude voided entries from business ledgers/reports (audit can show all via raw query).
      const dataFiltered = (data as any[]).filter((e: any) => e.is_void !== true);

      // CRITICAL: Filter out entries for deleted purchases/sales
      const purchaseIds = dataFiltered
        .filter(e => e.reference_type === 'purchase' && e.reference_id)
        .map(e => e.reference_id) as string[];
      
      const saleIds = dataFiltered
        .filter((e: any) =>
          (e.reference_type === 'sale' ||
            e.reference_type === 'sale_adjustment' ||
            e.reference_type === 'sale_reversal') &&
          e.reference_id
        )
        .map((e: any) => e.reference_id) as string[];
      
      // Include journal_entries.payment_id in the payments batch so supplier/customer settlement rows get PAY-xx / RCV-xx
      // into paymentRefNumberById (reference_type may still be purchase/sale on the JE header).
      // If RPC returns "Payment reference allocation failed after retries", verify migrations (generate_document_number /
      // record_payment_with_accounting) on the target DB and erp_document_sequences drift vs prod.
      const paymentIdsFromPaymentJe = dataFiltered
        .filter((e: any) => (e.reference_type === 'payment' || e.reference_type === 'payment_adjustment') && e.reference_id)
        .map((e: any) => e.reference_id) as string[];
      const paymentIdsFromJournalColumn = dataFiltered
        .map((e: any) => e.payment_id)
        .filter((id: unknown) => id != null && String(id).trim() !== '') as string[];
      const uniquePaymentIds = [...new Set([...paymentIdsFromPaymentJe, ...paymentIdsFromJournalColumn.map(String)])];

      // Check which purchases/sales still exist
      let existingPurchases: Set<string> = new Set();
      let existingSales: Set<string> = new Set();
      let validPayments: Set<string> = new Set();
      let paymentsList: { id: string; reference_type?: string; reference_id?: string; reference_number?: string | null }[] =
        [];

      if (purchaseIds.length > 0) {
        const purchases = await fetchInBatches(purchaseIds, async (chunk) => {
          const { data, error } = await supabase.from('purchases').select('id').in('id', chunk);
          if (error) throw error;
          return data || [];
        });
        existingPurchases = new Set(purchases.map((p: any) => p.id));
      }

      if (saleIds.length > 0) {
        const sales = await fetchInBatches(saleIds, async (chunk) => {
          const { data, error } = await supabase.from('sales').select('id').in('id', chunk);
          if (error) throw error;
          return data || [];
        });
        existingSales = new Set(sales.map((s: any) => s.id));
      }

      // For payments, check if the referenced purchase/sale exists (payment + payment_adjustment both use payment id)
      if (uniquePaymentIds.length > 0) {
        const payments = await fetchInBatches(uniquePaymentIds, async (chunk) => {
          const { data, error } = await supabase
            .from('payments')
            .select('id, reference_type, reference_id, reference_number, attachments')
            .in('id', chunk);
          if (error) throw error;
          return data || [];
        });

        if (payments.length > 0) {
          paymentsList = payments;
          const purchaseRefs = payments
            .filter((p: any) => p.reference_type === 'purchase' && p.reference_id)
            .map((p: any) => p.reference_id) as string[];

          const saleRefs = payments
            .filter((p: any) => p.reference_type === 'sale' && p.reference_id)
            .map((p: any) => p.reference_id) as string[];

          // Check if referenced purchases exist
          if (purchaseRefs.length > 0) {
            const purchaseChecks = await fetchInBatches(purchaseRefs, async (chunk) => {
              const { data, error } = await supabase.from('purchases').select('id').in('id', chunk);
              if (error) throw error;
              return data || [];
            });
            const validPurchaseIds = new Set(purchaseChecks.map((p: any) => p.id));
            payments
              .filter((p: any) => p.reference_type === 'purchase' && validPurchaseIds.has(p.reference_id))
              .forEach((p: any) => validPayments.add(p.id));
          }

          // Check if referenced sales exist
          if (saleRefs.length > 0) {
            const saleChecks = await fetchInBatches(saleRefs, async (chunk) => {
              const { data, error } = await supabase.from('sales').select('id').in('id', chunk);
              if (error) throw error;
              return data || [];
            });
            const validSaleIds = new Set(saleChecks.map((s: any) => s.id));
            payments
              .filter((p: any) => p.reference_type === 'sale' && validSaleIds.has(p.reference_id))
              .forEach((p: any) => validPayments.add(p.id));
          }

          // Payments without reference_type or with other types are valid
          payments
            .filter((p: any) => !p.reference_type || (p.reference_type !== 'purchase' && p.reference_type !== 'sale'))
            .forEach((p: any) => validPayments.add(p.id));
        }
      }

      // Filter entries: only include if purchase/sale/payment still exists (no per-entry logging on load)
      let validEntries = dataFiltered.filter((entry: any) => {
        if (entry.reference_type === 'purchase' && entry.reference_id && !existingPurchases.has(entry.reference_id)) return false;
        if (entry.reference_type === 'purchase_adjustment' && entry.reference_id && !existingPurchases.has(entry.reference_id)) return false;
        if (entry.reference_type === 'sale' && entry.reference_id && !existingSales.has(entry.reference_id)) return false;
        if (entry.reference_type === 'sale_adjustment' && entry.reference_id && !existingSales.has(entry.reference_id)) return false;
        if (entry.reference_type === 'payment' && entry.reference_id && !validPayments.has(entry.reference_id)) return false;
        if (entry.reference_type === 'payment_adjustment' && entry.reference_id && !validPayments.has(entry.reference_id)) return false;
        return true;
      });

      if (import.meta.env?.DEV && validEntries.length !== dataFiltered.length) {
        console.log(`[ACCOUNTING SERVICE] Filtered ${dataFiltered.length} entries to ${validEntries.length} valid entries`);
      }

      // List mode: skip party/attachment/stock enrichment (detail modal loads as needed).
      if (listMode) {
        // hasMore from raw fetch size BEFORE void/orphan filters — otherwise a full page
        // that shrinks below `limit` is wrongly treated as EOF (e.g. "98 of 98").
        const fetchedLen = (data as any[]).length;
        const hasMore = fetchedLen >= limit;
        const listTotal =
          listExactTotal ??
          count ??
          (hasMore ? offset + Math.max(validEntries.length, 1) + 1 : offset + validEntries.length);
        return opts ? { data: validEntries, total: listTotal } : validEntries;
      }

      // Party name on payment row → journal list / workbench can show "AP — Supplier" without extra round-trips in UI.
      const payIds = [...new Set(validEntries.map((e: any) => e.payment_id).filter(Boolean))] as string[];
      const paymentContactNameByPaymentId = new Map<string, string>();
      if (payIds.length > 0) {
        const payPartyRows = await fetchInBatches(payIds, async (chunk) => {
          const { data, error: pErr } = await supabase
            .from('payments')
            .select('id, contact_id, contact:contacts(name)')
            .in('id', chunk);
          if (pErr) throw pErr;
          return data || [];
        });
        payPartyRows.forEach((p: any) => {
          const c = Array.isArray(p.contact) ? p.contact[0] : p.contact;
          const nm = c?.name && String(c.name).trim();
          if (nm) paymentContactNameByPaymentId.set(String(p.id), nm);
        });
      }
      validEntries = validEntries.map((e: any) => ({
        ...e,
        _payment_contact_name: e.payment_id ? paymentContactNameByPaymentId.get(String(e.payment_id)) : undefined,
      }));

      const purchaseIdsForParty = [
        ...new Set(
          validEntries
            .filter((e: any) => {
              const rt = String(e.reference_type || '').toLowerCase();
              return (rt === 'purchase' || rt === 'purchase_adjustment') && e.reference_id;
            })
            .map((e: any) => String(e.reference_id))
        ),
      ] as string[];
      const purchaseSupplierNameByPurchaseId = new Map<string, string>();
      if (purchaseIdsForParty.length > 0) {
        const purs = await fetchInBatches(purchaseIdsForParty, async (chunk) => {
          const { data, error: purErr } = await supabase
            .from('purchases')
            .select('id, supplier_id')
            .in('id', chunk);
          if (purErr) throw purErr;
          return data || [];
        });
        const supIds = [...new Set(purs.map((p: any) => p.supplier_id).filter(Boolean))] as string[];
        if (supIds.length > 0) {
          const supContacts = await fetchInBatches(supIds, async (chunk) => {
            const { data, error: cErr } = await supabase.from('contacts').select('id, name').in('id', chunk);
            if (cErr) throw cErr;
            return data || [];
          });
          const nmBySup = new Map(supContacts.map((c: any) => [String(c.id), String(c.name || '').trim()]));
          purs.forEach((p: any) => {
            const nm = p.supplier_id ? nmBySup.get(String(p.supplier_id)) : '';
            if (nm) purchaseSupplierNameByPurchaseId.set(String(p.id), nm);
          });
        }
      }
      const purchasePartyRt = (t: string) =>
        t === 'purchase' || t === 'purchase_adjustment' || t === 'purchase_return' || t === 'purchase_reversal';

      const purchaseReturnIdsForParty = [
        ...new Set(
          validEntries
            .filter((e: any) => {
              const rt = String(e.reference_type || '')
                .toLowerCase()
                .trim()
                .replace(/\s+/g, '_');
              return rt === 'purchase_return' && e.reference_id;
            })
            .map((e: any) => String(e.reference_id)),
        ),
      ] as string[];
      const purchaseReturnSupplierNameByReturnId = new Map<string, string>();
      if (purchaseReturnIdsForParty.length > 0) {
        const { data: pretRows } = await supabase
          .from('purchase_returns')
          .select('id, supplier_name, supplier_id')
          .in('id', purchaseReturnIdsForParty);
        (pretRows || []).forEach((r: any) => {
          const nm = (r.supplier_name && String(r.supplier_name).trim()) || '';
          if (nm) purchaseReturnSupplierNameByReturnId.set(String(r.id), nm);
        });
      }

      const saleReturnIdsForParty = [
        ...new Set(
          validEntries
            .filter((e: any) => {
              const rt = String(e.reference_type || '')
                .toLowerCase()
                .trim()
                .replace(/\s+/g, '_');
              return rt === 'sale_return' && e.reference_id;
            })
            .map((e: any) => String(e.reference_id)),
        ),
      ] as string[];
      const saleReturnCustomerNameByReturnId = new Map<string, string>();
      if (saleReturnIdsForParty.length > 0) {
        const { data: sretRows } = await supabase
          .from('sale_returns')
          .select('id, customer_name, customer_id')
          .in('id', saleReturnIdsForParty);
        (sretRows || []).forEach((r: any) => {
          const nm = (r.customer_name && String(r.customer_name).trim()) || '';
          if (nm) saleReturnCustomerNameByReturnId.set(String(r.id), nm);
        });
      }

      const paymentRefTypeByPaymentId = new Map<string, string>();
      const paymentRefNumberById = new Map<string, string>();
      (paymentsList || []).forEach((p: any) => {
        if (p?.id && p.reference_type) paymentRefTypeByPaymentId.set(String(p.id), String(p.reference_type));
        const rn = p?.reference_number != null ? String(p.reference_number).trim() : '';
        if (p?.id && rn) paymentRefNumberById.set(String(p.id), rn);
      });

      const saleIdsForInvoice = new Set<string>((saleIds || []).map((id: string) => String(id)));
      (paymentsList || []).forEach((p: any) => {
        if (p.reference_type === 'sale' && p.reference_id) saleIdsForInvoice.add(String(p.reference_id));
      });
      const purchaseIdsForPo = new Set<string>((purchaseIds || []).map((id: string) => String(id)));
      (paymentsList || []).forEach((p: any) => {
        if (p.reference_type === 'purchase' && p.reference_id) purchaseIdsForPo.add(String(p.reference_id));
      });
      const expenseIdsForNo = new Set<string>();
      validEntries.forEach((e: any) => {
        const rtN = String(e.reference_type || '')
          .toLowerCase()
          .trim()
          .replace(/\s+/g, '_');
        if (rtN === 'expense' && e.reference_id) expenseIdsForNo.add(String(e.reference_id));
        if ((rtN === 'sale' || rtN === 'sale_reversal') && e.reference_id) {
          saleIdsForInvoice.add(String(e.reference_id));
        }
      });
      (paymentsList || []).forEach((p: any) => {
        if (p.reference_type === 'expense' && p.reference_id) expenseIdsForNo.add(String(p.reference_id));
      });

      const saleInvoiceNoById = new Map<string, string>();
      const saleStatusById = new Map<string, string>();
      const saleAttachmentsById = new Map<string, TransactionAttachment[]>();
      if (saleIdsForInvoice.size > 0) {
        const { data: saleRows } = await supabase
          .from('sales')
          .select('id, invoice_no, status, attachments')
          .in('id', [...saleIdsForInvoice]);
        (saleRows || []).forEach((s: any) => {
          if (s?.id && s.invoice_no) saleInvoiceNoById.set(String(s.id), String(s.invoice_no));
          if (s?.id && s.status) saleStatusById.set(String(s.id), String(s.status));
          if (s?.id && s.attachments) {
            saleAttachmentsById.set(String(s.id), normalizeAttachmentList(s.attachments));
          }
        });
      }

      const purchasePoNoById = new Map<string, string>();
      const purchaseAttachmentsById = new Map<string, TransactionAttachment[]>();
      if (purchaseIdsForPo.size > 0) {
        const { data: purRows } = await supabase
          .from('purchases')
          .select('id, po_no, attachments')
          .in('id', [...purchaseIdsForPo]);
        (purRows || []).forEach((r: any) => {
          if (r?.id && r.po_no) purchasePoNoById.set(String(r.id), String(r.po_no));
          if (r?.id && r.attachments) {
            purchaseAttachmentsById.set(String(r.id), normalizeAttachmentList(r.attachments));
          }
        });
      }

      const expenseNoById = new Map<string, string>();
      const expenseReceiptById = new Map<string, string>();
      if (expenseIdsForNo.size > 0) {
        const { data: expRows } = await supabase
          .from('expenses')
          .select('id, expense_no, receipt_url')
          .in('id', [...expenseIdsForNo]);
        (expRows || []).forEach((r: any) => {
          if (r?.id && r.expense_no) expenseNoById.set(String(r.id), String(r.expense_no).trim());
          if (r?.id && r.receipt_url) expenseReceiptById.set(String(r.id), String(r.receipt_url).trim());
        });
      }

      const paymentAttachmentsById = new Map<string, TransactionAttachment[]>();
      (paymentsList || []).forEach((p: any) => {
        if (p?.id && p.attachments) {
          paymentAttachmentsById.set(String(p.id), normalizeAttachmentList(p.attachments));
        }
      });

      const stockAdjMovementIds = [
        ...new Set(
          validEntries
            .filter(
              (e: { reference_type?: string; reference_id?: string }) =>
                String(e.reference_type || '').toLowerCase() === 'stock_adjustment' && e.reference_id
            )
            .map((e: { reference_id?: string }) => String(e.reference_id))
        ),
      ] as string[];
      const stockMovementAmountById = new Map<string, number>();
      if (stockAdjMovementIds.length > 0) {
        try {
          const movements = await fetchInBatches(stockAdjMovementIds, async (chunk) => {
            const { data, error } = await supabase
              .from('stock_movements')
              .select('id, quantity, unit_cost, total_cost, product_id, variation_id')
              .in('id', chunk);
            if (error) throw error;
            return data || [];
          });
          const productIds = [
            ...new Set(
              movements
                .map((m: { product_id?: string }) => m.product_id)
                .filter((id): id is string => id != null && String(id).trim() !== '')
            ),
          ];
          const variationIds = [
            ...new Set(
              movements
                .map((m: { variation_id?: string | null }) => m.variation_id)
                .filter((id): id is string => id != null && String(id).trim() !== '')
            ),
          ];
          const productCostById = new Map<string, number>();
          if (productIds.length > 0) {
            const { data: prods } = await supabase
              .from('products')
              .select('id, cost_price')
              .in('id', productIds);
            (prods || []).forEach((p: { id?: string; cost_price?: number }) => {
              if (p?.id) productCostById.set(String(p.id), Number(p.cost_price) || 0);
            });
          }
          const variationCostById = new Map<string, { cost: number; purchase: number }>();
          if (variationIds.length > 0) {
            const { data: vars } = await supabase
              .from('product_variations')
              .select('id, cost_price, purchase_price')
              .in('id', variationIds);
            (vars || []).forEach((v: { id?: string; cost_price?: number; purchase_price?: number }) => {
              if (v?.id) {
                variationCostById.set(String(v.id), {
                  cost: Number(v.cost_price) || 0,
                  purchase: Number(v.purchase_price) || 0,
                });
              }
            });
          }
          for (const m of movements) {
            const mid = String((m as { id?: string }).id || '');
            if (!mid) continue;
            const vid = (m as { variation_id?: string | null }).variation_id;
            const pid = (m as { product_id?: string }).product_id;
            const vcost = vid ? variationCostById.get(String(vid)) : undefined;
            const displayAmt = resolveStockMovementDisplayAmount({
              ...m,
              product_cost_price: pid ? productCostById.get(String(pid)) : 0,
              variation_cost_price: vcost?.cost,
              variation_purchase_price: vcost?.purchase,
            });
            if (displayAmt > 0) stockMovementAmountById.set(mid, displayAmt);
          }
        } catch (stockEnrichErr) {
          console.warn('[ACCOUNTING SERVICE] stock_adjustment movement enrich:', stockEnrichErr);
        }
      }

      validEntries = validEntries.map((e: any) => {
        const rtNorm = String(e.reference_type || '')
          .toLowerCase()
          .trim()
          .replace(/\s+/g, '_');
        let supName: string | undefined;
        let saleRetCustomerName: string | undefined;
        if (rtNorm === 'purchase_return' && e.reference_id) {
          supName = purchaseReturnSupplierNameByReturnId.get(String(e.reference_id));
        } else if (rtNorm === 'sale_return' && e.reference_id) {
          saleRetCustomerName = saleReturnCustomerNameByReturnId.get(String(e.reference_id));
        } else if (purchasePartyRt(rtNorm) && e.reference_id) {
          supName = purchaseSupplierNameByPurchaseId.get(String(e.reference_id));
        }
        const linkedPayRt =
          e.payment_id
            ? paymentRefTypeByPaymentId.get(String(e.payment_id))
            : e.reference_type === 'payment_adjustment' && e.reference_id
              ? paymentRefTypeByPaymentId.get(String(e.reference_id))
              : undefined;
        return {
          ...e,
          _purchase_supplier_name: supName,
          _sale_return_customer_name: saleRetCustomerName,
          _linked_payment_reference_type: linkedPayRt,
        };
      });

      // PF-14.3B: Root-document grouping – attach root_reference_type and root_reference_id so Journal list can show one logical row per sale
      const paymentIdToRoot = new Map<string, { root_reference_type: string; root_reference_id: string }>();
      paymentsList.forEach((p: any) => {
          if (p.reference_type === 'sale' && p.reference_id) {
            paymentIdToRoot.set(p.id, { root_reference_type: 'sale', root_reference_id: p.reference_id });
          }
          if (p.reference_type === 'purchase' && p.reference_id) {
            paymentIdToRoot.set(p.id, { root_reference_type: 'purchase', root_reference_id: p.reference_id });
          }
      });
      const enrichedEntries = validEntries.map((entry: any) => {
        const out = { ...entry };
        if (entry.reference_type === 'sale' || entry.reference_type === 'sale_adjustment') {
          if (entry.reference_id) {
            out.root_reference_type = 'sale';
            out.root_reference_id = entry.reference_id;
          }
        } else if (entry.reference_type === 'payment' || entry.reference_type === 'payment_adjustment') {
          const payKey = entry.payment_id || entry.reference_id;
          if (payKey) {
            const root = paymentIdToRoot.get(String(payKey));
            if (root) {
              out.root_reference_type = root.root_reference_type;
              out.root_reference_id = root.root_reference_id;
            }
          }
        } else if ((entry.reference_type === 'purchase' || entry.reference_type === 'purchase_adjustment') && entry.reference_id) {
          out.root_reference_type = 'purchase';
          out.root_reference_id = entry.reference_id;
        } else if (
          String(entry.reference_type || '')
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '_') === 'purchase_return' &&
          entry.reference_id
        ) {
          out.root_reference_type = 'purchase_return';
          out.root_reference_id = entry.reference_id;
        } else if (
          String(entry.reference_type || '')
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '_') === 'sale_return' &&
          entry.reference_id
        ) {
          out.root_reference_type = 'sale_return';
          out.root_reference_id = entry.reference_id;
        }
        const rtN = String(entry.reference_type || '')
          .toLowerCase()
          .trim()
          .replace(/\s+/g, '_');
        let opSaleInv: string | undefined;
        let opPurPo: string | undefined;
        let opExpenseNo: string | undefined;
        if ((rtN === 'sale' || rtN === 'sale_adjustment') && entry.reference_id) {
          opSaleInv = saleInvoiceNoById.get(String(entry.reference_id));
        } else if (rtN === 'sale_reversal' && entry.reference_id) {
          opSaleInv = saleInvoiceNoById.get(String(entry.reference_id));
        } else if ((rtN === 'purchase' || rtN === 'purchase_adjustment') && entry.reference_id) {
          opPurPo = purchasePoNoById.get(String(entry.reference_id));
        } else if (rtN === 'expense' && entry.reference_id) {
          opExpenseNo = expenseNoById.get(String(entry.reference_id));
        }
        const payKeyForOps =
          entry.payment_id ||
          ((rtN === 'payment' || rtN === 'payment_adjustment') && entry.reference_id
            ? String(entry.reference_id)
            : undefined);
        if (payKeyForOps) {
          const payRn = paymentRefNumberById.get(String(payKeyForOps));
          if (payRn) out._payment_reference_number = payRn;
          const payRow = (paymentsList || []).find((p: any) => String(p.id) === String(payKeyForOps));
          if (payRow?.amount != null) out._payment_amount = Number(payRow.amount) || 0;
          const linkedPayRt = paymentRefTypeByPaymentId.get(String(payKeyForOps));
          if (linkedPayRt === 'expense') {
            if (payRow?.reference_id) {
              opExpenseNo = opExpenseNo || expenseNoById.get(String(payRow.reference_id));
            }
          }
          const root = paymentIdToRoot.get(String(payKeyForOps));
          if (root?.root_reference_type === 'sale' && root.root_reference_id) {
            opSaleInv = opSaleInv || saleInvoiceNoById.get(String(root.root_reference_id));
          }
          if (root?.root_reference_type === 'purchase' && root.root_reference_id) {
            opPurPo = opPurPo || purchasePoNoById.get(String(root.root_reference_id));
          }
        }
        if (opSaleInv) out._display_sale_invoice_no = opSaleInv;
        if (opPurPo) out._display_purchase_po_no = opPurPo;
        if (opExpenseNo) out._display_expense_no = opExpenseNo;
        const saleRefId =
          (rtN === 'sale' || rtN === 'sale_adjustment' || rtN === 'sale_reversal') && entry.reference_id
            ? String(entry.reference_id)
            : undefined;
        if (saleRefId && saleStatusById.has(saleRefId)) {
          out._linked_sale_status = saleStatusById.get(saleRefId);
        }
        const sourceAttachmentParts: unknown[] = [];
        if (rtN === 'expense' && entry.reference_id) {
          const receipt = expenseReceiptById.get(String(entry.reference_id));
          if (receipt) sourceAttachmentParts.push(...receiptUrlToAttachments(receipt));
        } else if ((rtN === 'sale' || rtN === 'sale_adjustment' || rtN === 'sale_reversal') && entry.reference_id) {
          const saleAtt = saleAttachmentsById.get(String(entry.reference_id));
          if (saleAtt?.length) sourceAttachmentParts.push(...saleAtt);
        } else if (
          (rtN === 'purchase' || rtN === 'purchase_adjustment' || rtN === 'purchase_return') &&
          entry.reference_id
        ) {
          const purAtt = purchaseAttachmentsById.get(String(entry.reference_id));
          if (purAtt?.length) sourceAttachmentParts.push(...purAtt);
        }
        if (payKeyForOps) {
          const payAtt = paymentAttachmentsById.get(String(payKeyForOps));
          if (payAtt?.length) sourceAttachmentParts.push(...payAtt);
          const linkedPayRtForAtt = paymentRefTypeByPaymentId.get(String(payKeyForOps));
          const payRowForAtt = (paymentsList || []).find((p: any) => String(p.id) === String(payKeyForOps));
          if (linkedPayRtForAtt === 'expense' && payRowForAtt?.reference_id) {
            const receipt = expenseReceiptById.get(String(payRowForAtt.reference_id));
            if (receipt) sourceAttachmentParts.push(...receiptUrlToAttachments(receipt));
          }
        }
        const isPaymentSettlementRow = !!(payKeyForOps || entry.payment_id);
        const rootRefId = out.root_reference_id;
        const rootRefType = String(out.root_reference_type || '').toLowerCase();
        if (!isPaymentSettlementRow) {
          if (rootRefId && rootRefType === 'sale') {
            const saleAtt = saleAttachmentsById.get(String(rootRefId));
            if (saleAtt?.length) sourceAttachmentParts.push(...saleAtt);
          } else if (rootRefId && rootRefType === 'purchase') {
            const purAtt = purchaseAttachmentsById.get(String(rootRefId));
            if (purAtt?.length) sourceAttachmentParts.push(...purAtt);
          }
        }
        const mergedSource = mergeAttachmentLists(sourceAttachmentParts);
        if (mergedSource.length > 0) out._source_attachments = mergedSource;
        if (rtN === 'stock_adjustment' && entry.reference_id) {
          const smAmt = stockMovementAmountById.get(String(entry.reference_id));
          if (smAmt != null && smAmt > 0) out._stock_movement_amount = smAmt;
        }
        return out;
      });

      return opts ? { data: enrichedEntries, total: count ?? enrichedEntries.length } : enrichedEntries;
    } catch (error: any) {
      console.warn('[ACCOUNTING SERVICE] Error:', error?.message || error);
      // Paginated callers must not treat enrichment/query failure as an empty period
      if (opts) throw error;
      return [];
    }
  },

  // Get single journal entry with lines
  async getEntry(id: string) {
    const { data, error } = await supabase
      .from('journal_entries')
      .select(`
        *,
        lines:journal_entry_lines(*)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  /** Incremental refresh: journal rows for a sale document + its payment JEs (avoids full getAllEntries). */
  async fetchJournalEntriesForSale(
    companyId: string,
    saleId: string,
    branchId?: string
  ): Promise<any[]> {
    if (!companyId || !saleId) return [];
    const jeSelect = `
      *,
      lines:journal_entry_lines(
        id,
        journal_entry_id,
        account_id,
        debit,
        credit,
        description,
        account:accounts(name, code, type)
      )
    `;
    let saleQuery = supabase
      .from('journal_entries')
      .select(jeSelect)
      .eq('company_id', companyId)
      .eq('reference_id', saleId)
      .in('reference_type', ['sale', 'sale_adjustment']);
    if (branchId) {
      saleQuery = saleQuery.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }
    const { data: saleRows, error: saleErr } = await saleQuery;
    if (saleErr && import.meta.env?.DEV) {
      console.warn('[ACCOUNTING SERVICE] fetchJournalEntriesForSale:', saleErr.message);
    }
    const out: any[] = ((saleRows || []) as any[]).filter((e) => e.is_void !== true);

    const { data: payments } = await supabase
      .from('payments')
      .select('id')
      .eq('reference_type', 'sale')
      .eq('reference_id', saleId);
    const payIds = [...new Set((payments || []).map((p: any) => String(p.id)).filter(Boolean))];
    if (payIds.length > 0) {
      let payQuery = supabase
        .from('journal_entries')
        .select(jeSelect)
        .eq('company_id', companyId)
        .in('reference_id', payIds)
        .in('reference_type', ['payment', 'payment_adjustment']);
      if (branchId) {
        payQuery = payQuery.or(`branch_id.eq.${branchId},branch_id.is.null`);
      }
      const { data: payRows } = await payQuery;
      for (const row of (payRows || []) as any[]) {
        if (row.is_void !== true) out.push(row);
      }
    }
    const byId = new Map<string, any>();
    for (const row of out) {
      if (row?.id) byId.set(String(row.id), row);
    }
    return Array.from(byId.values());
  },

  /** Incremental refresh: journal rows for an expense document (avoids full getAllEntries). */
  async fetchJournalEntriesForExpense(
    companyId: string,
    expenseId: string,
    branchId?: string
  ): Promise<any[]> {
    if (!companyId || !expenseId) return [];
    const jeSelect = `
      *,
      lines:journal_entry_lines(
        id,
        journal_entry_id,
        account_id,
        debit,
        credit,
        description,
        account:accounts(name, code, type)
      )
    `;
    let query = supabase
      .from('journal_entries')
      .select(jeSelect)
      .eq('company_id', companyId)
      .eq('reference_id', expenseId)
      .eq('reference_type', 'expense');
    if (branchId) {
      query = query.or(`branch_id.eq.${branchId},branch_id.is.null`);
    }
    const { data, error } = await query;
    if (error && import.meta.env?.DEV) {
      console.warn('[ACCOUNTING SERVICE] fetchJournalEntriesForExpense:', error.message);
    }
    const rows = ((data || []) as any[]).filter((e) => e.is_void !== true);
    if (rows.length === 0) return rows;
    const { data: expRow } = await supabase
      .from('expenses')
      .select('receipt_url')
      .eq('id', expenseId)
      .eq('company_id', companyId)
      .maybeSingle();
    const receipt = expRow?.receipt_url ? String(expRow.receipt_url).trim() : '';
    if (!receipt) return rows;
    const sourceAtt = receiptUrlToAttachments(receipt);
    return rows.map((row) => ({
      ...row,
      _source_attachments: mergeAttachmentLists(
        (row as { _source_attachments?: unknown })._source_attachments,
        sourceAtt
      ),
    }));
  },

  /**
   * PF-14.4: Idempotency – check if a sale_adjustment JE already exists for this sale with the same description.
   * Prevents duplicate JEs when postSaleEditAdjustments is called multiple times for the same logical edit.
   */
  async hasExistingSaleAdjustmentByDescription(
    companyId: string,
    saleId: string,
    description: string
  ): Promise<boolean> {
    if (!companyId || !saleId || !description?.trim()) return false;
    const { data, error } = await supabase
      .from('journal_entries')
      .select('id')
      .eq('company_id', companyId)
      .eq('reference_type', 'sale_adjustment')
      .eq('reference_id', saleId)
      .eq('description', description)
      .or('is_void.is.null,is_void.eq.false')
      .limit(1);
    if (error) return false;
    return Array.isArray(data) && data.length > 0;
  },

  /**
   * PF-COMPONENT: Idempotency for purchase_adjustment JEs (component-level edit engine).
   */
  async hasExistingPurchaseAdjustmentByDescription(
    companyId: string,
    purchaseId: string,
    description: string
  ): Promise<boolean> {
    if (!companyId || !purchaseId || !description?.trim()) return false;
    const { data, error } = await supabase
      .from('journal_entries')
      .select('id')
      .eq('company_id', companyId)
      .eq('reference_type', 'purchase_adjustment')
      .eq('reference_id', purchaseId)
      .eq('description', description)
      .or('is_void.is.null,is_void.eq.false')
      .limit(1);
    if (error) return false;
    return Array.isArray(data) && data.length > 0;
  },

  /**
   * PF-14.4: Idempotency – check if a payment_adjustment JE already exists for this payment with same amount edit.
   * Matches description pattern "was Rs X, now Rs Y" so duplicate amount edits do not create extra JEs.
   */
  async hasExistingPaymentAmountAdjustment(
    companyId: string,
    paymentId: string,
    oldAmount: number,
    newAmount: number,
    /** When set, idempotency prefers action_fingerprint including this liquidity account (PF-14 hotfix). */
    liquidityAccountId?: string | null
  ): Promise<boolean> {
    if (!companyId || !paymentId) return false;
    const o = Number(oldAmount);
    const n = Number(newAmount);
    const needle1 = `was Rs ${o.toLocaleString()}, now Rs ${n.toLocaleString()}`;
    const needle2 = `was Rs ${o}, now Rs ${n}`;
    const expectedFp =
      liquidityAccountId && String(liquidityAccountId).trim()
        ? `payment_adjustment_amount:${companyId}:${paymentId}:${o}:${n}:${String(liquidityAccountId).trim()}`
        : null;
    const { data, error } = await supabase
      .from('journal_entries')
      .select('id, description, action_fingerprint')
      .eq('company_id', companyId)
      .eq('reference_type', 'payment_adjustment')
      .eq('reference_id', paymentId)
      .or('is_void.is.null,is_void.eq.false');
    if (error || !data?.length) return false;
    return data.some((row: { description?: string; action_fingerprint?: string | null }) => {
      const fp = String(row.action_fingerprint || '').trim();
      if (expectedFp && fp === expectedFp) return true;
      const d = row.description || '';
      if (!fp && (d.includes(needle1) || d.includes(needle2))) return true;
      return false;
    });
  },

  /**
   * PF-14.4: Idempotency – check if a payment_adjustment JE already exists for this payment moving
   * the same amount from oldAccountId to newAccountId (Dr new, Cr old). Prevents duplicate
   * "Payment account changed" JEs from syncPaymentAccountAdjustmentsForCompany or repeated UI saves.
   */
  async hasExistingPaymentAccountAdjustment(
    companyId: string,
    paymentId: string,
    oldAccountId: string,
    newAccountId: string,
    amount: number
  ): Promise<boolean> {
    if (!companyId || !paymentId || !oldAccountId || !newAccountId || amount <= 0) return false;
    const { data: entries, error } = await supabase
      .from('journal_entries')
      .select('id')
      .eq('company_id', companyId)
      .eq('reference_type', 'payment_adjustment')
      .eq('reference_id', paymentId)
      .ilike('description', '%Payment account changed%')
      .or('is_void.is.null,is_void.eq.false');
    if (error || !entries?.length) return false;
    const jeIds = entries.map((e: { id: string }) => e.id);
    let lines: { journal_entry_id: string; account_id: string; debit: number; credit: number }[] = [];
    try {
      lines = await fetchInBatches(jeIds, async (chunk) => {
        const { data, error: lineErr } = await supabase
          .from('journal_entry_lines')
          .select('journal_entry_id, account_id, debit, credit')
          .in('journal_entry_id', chunk);
        if (lineErr) throw lineErr;
        return data || [];
      });
    } catch {
      return false;
    }
    if (!lines.length) return false;
    const amountRounded = Math.round(amount * 100) / 100;
    const byJe = new Map<string, { debit: Map<string, number>; credit: Map<string, number> }>();
    for (const line of lines as { journal_entry_id: string; account_id: string; debit: number; credit: number }[]) {
      if (!byJe.has(line.journal_entry_id)) {
        byJe.set(line.journal_entry_id, { debit: new Map(), credit: new Map() });
      }
      const rec = byJe.get(line.journal_entry_id)!;
      const d = (rec.debit.get(line.account_id) ?? 0) + Number(line.debit ?? 0);
      const c = (rec.credit.get(line.account_id) ?? 0) + Number(line.credit ?? 0);
      rec.debit.set(line.account_id, d);
      rec.credit.set(line.account_id, c);
    }
    for (const [, rec] of byJe) {
      const newDebit = rec.debit.get(newAccountId) ?? 0;
      const oldCredit = rec.credit.get(oldAccountId) ?? 0;
      if (Math.abs(newDebit - amountRounded) < 0.02 && Math.abs(oldCredit - amountRounded) < 0.02) return true;
    }
    return false;
  },

  /** Active non-void `correction_reversal` JE whose `reference_id` points at this original header id (PF-07). */
  async findActiveCorrectionReversalJournalId(
    companyId: string,
    originalJournalEntryId: string
  ): Promise<string | null> {
    if (!companyId || !originalJournalEntryId) return null;
    const { data, error } = await supabase
      .from('journal_entries')
      .select('id')
      .eq('company_id', companyId)
      .eq('reference_type', 'correction_reversal')
      .eq('reference_id', originalJournalEntryId)
      .or('is_void.is.null,is_void.eq.false')
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();
    if (error || !data) return null;
    return (data as { id: string }).id;
  },

  /**
   * Create a safe reversal journal entry for manual correction (PF-07).
   *
   * **Single-JE (no chain):** swapped debit/credit mirror of the original, posted as
   * `correction_reversal`.
   *
   * **Multi-member payment chain:** void ALL chain members (primary + PF-14 adjustments)
   * so the entire chain nets to zero.  No separate composite reversal JE is needed —
   * the voided rows *are* the cancellation record.  The payment row is also voided.
   *
   * Idempotent: at most one active correction_reversal per original JE.
   */
  async createReversalEntry(
    companyId: string,
    branchId: string | null,
    originalJournalEntryId: string,
    createdBy?: string | null,
    reason?: string,
    /** Sale/purchase return void and other module flows must post reversal JEs while Journal UI stays blocked. */
    options?: { bypassJournalSourceControlPolicy?: boolean }
  ): Promise<{ id: string; alreadyExisted: boolean } | null> {
    console.log('[PAYMENT_REVERSAL_TRACE]', JSON.stringify({ phase: 'start', companyId, originalJournalEntryId }));
    const existingId = await this.findActiveCorrectionReversalJournalId(companyId, originalJournalEntryId);
    if (existingId) {
      console.log('[PAYMENT_REVERSAL_TRACE]', JSON.stringify({ phase: 'already_exists', originalJournalEntryId, reversalId: existingId }));
      try {
        const existingOriginal = await this.getEntry(originalJournalEntryId).catch(() => null);
        const origRef = String((existingOriginal as any)?.reference_type || '').toLowerCase();
        if (origRef === 'fx_currency_purchase') {
          const { finalizeFxCurrencyPurchaseCreditAfterReversal } = await import(
            '@/app/services/importFxAgentService'
          );
          await finalizeFxCurrencyPurchaseCreditAfterReversal({
            companyId,
            originalJournalEntryId,
            reversalJournalEntryId: existingId,
            reason: reason?.trim() || 'Path 21: FX credit journal reversed',
          });
        }
      } catch (fxVoidErr: any) {
        console.warn(
          '[ACCOUNTING] Existing reversal found but Path 21 FX credit finalize failed:',
          fxVoidErr?.message || fxVoidErr
        );
      }
      return { id: existingId, alreadyExisted: true };
    }

    const original = await this.getEntry(originalJournalEntryId).catch(() => null);
    if (!original || (original as any).company_id !== companyId) {
      return null;
    }
    const lines = (original as any).lines;
    if (!Array.isArray(lines) || lines.length === 0) {
      return null;
    }

    if (!options?.bypassJournalSourceControlPolicy) {
      const { journalReversalBlockedReason } = await import('@/app/lib/journalEntryEditPolicy');
      const payEmbedded = (original as any).payment;
      const payObj = Array.isArray(payEmbedded) ? payEmbedded[0] : payEmbedded;
      const reversalPolicyBlock = journalReversalBlockedReason(
        {
          reference_type: (original as any).reference_type,
          reference_id: (original as any).reference_id,
          payment_id: (original as any).payment_id,
          is_void: (original as any).is_void,
          payment_chain_is_historical: (original as any).payment_chain_is_historical,
          hasActiveCorrectionReversal: false,
        },
        payObj ?? undefined
      );
      if (reversalPolicyBlock) {
        throw new Error(reversalPolicyBlock);
      }
    }

    const {
      extractPaymentChainIdFromJournalRow,
      fetchPaymentChainState,
      HISTORICAL_PREFIX,
    } = await import('@/app/services/paymentChainMutationGuard');
    const chainPaymentId = extractPaymentChainIdFromJournalRow(original as any);
    let chainMemberCount = 0;
    if (chainPaymentId) {
      const chainState = await fetchPaymentChainState(companyId, chainPaymentId);
      chainMemberCount = chainState.memberCount;
      if (chainState.tailJournalId && chainState.tailJournalId !== originalJournalEntryId) {
        throw new Error(
          HISTORICAL_PREFIX +
            'This payment line is historical (a later edit or transfer exists). Open the latest journal row for this receipt to reverse.'
        );
      }
    }

    // ── Multi-member payment chain: void entire chain (no extra reversal JE) ──
    if (chainPaymentId && chainMemberCount > 1) {
      console.log('[PAYMENT_REVERSAL_TRACE]', JSON.stringify({ phase: 'chain_void_all', paymentId: chainPaymentId, members: chainMemberCount }));
      try {
        const pid = chainPaymentId;
        const { data: prow } = await supabase
          .from('payments')
          .select('id, reference_type, voided_at, contact_id')
          .eq('id', pid)
          .eq('company_id', companyId)
          .maybeSingle();
        const rt = String((prow as any)?.reference_type || '').toLowerCase();
        const canVoidRow = prow && !(prow as any).voided_at;
        if (canVoidRow) {
          const { voidPaymentAfterJournalReversal } = await import('@/app/services/paymentLifecycleService');
          await voidPaymentAfterJournalReversal({ companyId, paymentId: pid });
          console.log('[PAYMENT_REVERSAL_TRACE]', JSON.stringify({ phase: 'chain_voided', paymentId: pid, reference_type: rt }));
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('paymentAdded'));
            if (rt === 'manual_payment' || rt === 'purchase') {
              const cid = (prow as any).contact_id as string | null | undefined;
              window.dispatchEvent(new CustomEvent('ledgerUpdated', { detail: { ledgerType: 'supplier', entityId: cid || undefined } }));
            } else {
              window.dispatchEvent(new CustomEvent('ledgerUpdated', { detail: { ledgerType: 'customer' } }));
            }
          }
        }
      } catch (voidErr: any) {
        console.warn('[ACCOUNTING] Chain void failed:', voidErr?.message || voidErr);
      }
      const { recordTransactionMutation } = await import('@/app/services/transactionMutationService');
      void recordTransactionMutation({
        companyId,
        branchId,
        entityType: 'payment',
        entityId: chainPaymentId,
        mutationType: 'reversal',
        reason: reason?.trim() || 'Full payment chain reversal — all chain members voided',
      });
      return { id: originalJournalEntryId, alreadyExisted: false };
    }

    // ── Single JE (no chain): classic mirror reversal ──
    const entryNo = `JE-REV-${Date.now()}-${Math.random().toString(36).substr(2, 5).toUpperCase()}`;
    const entryDate = new Date().toISOString().split('T')[0];
    const defaultDescription = reason?.trim()
      ? `Reversal: ${reason}`
      : `Reversal of: ${(original as any).description || (original as any).entry_no || 'Journal entry'}`;

    // Resolve parent→sub-ledger mapping for AR/AP lines.
    // When original JE used parent 1100/2000 but a sub-ledger exists for the linked party,
    // the reversal must use the same sub-ledger to keep customer/supplier ledger balanced.
    let parentToSubLedgerMap: Record<string, string> = {};
    try {
      const origRefType = String((original as any).reference_type || '').toLowerCase();
      const origRefId = (original as any).reference_id;
      if (origRefType === 'sale' && origRefId) {
        const { data: saleRow } = await supabase.from('sales').select('customer_id').eq('id', origRefId).maybeSingle();
        const custId = (saleRow as any)?.customer_id;
        if (custId) {
          const { resolveReceivablePostingAccountId } = await import('@/app/services/partySubledgerAccountService');
          const subId = await resolveReceivablePostingAccountId(companyId, custId);
          if (subId) {
            // Find parent AR (1100) id to map
            const { accountService: acctSvc } = await import('@/app/services/accountService');
            const allAccts = await acctSvc.getAllAccounts(companyId);
            const parent1100 = (allAccts || []).find((a: any) => String(a.code || '').trim() === '1100');
            if (parent1100?.id && subId !== parent1100.id) {
              parentToSubLedgerMap[parent1100.id as string] = subId;
            }
          }
        }
      } else if (origRefType === 'purchase' && origRefId) {
        const { data: purRow } = await supabase.from('purchases').select('supplier_id').eq('id', origRefId).maybeSingle();
        const supId = (purRow as any)?.supplier_id;
        if (supId) {
          const { resolvePayablePostingAccountId } = await import('@/app/services/partySubledgerAccountService');
          const subId = await resolvePayablePostingAccountId(companyId, supId);
          if (subId) {
            const { accountService: acctSvc } = await import('@/app/services/accountService');
            const allAccts = await acctSvc.getAllAccounts(companyId);
            const parent2000 = (allAccts || []).find((a: any) => String(a.code || '').trim() === '2000');
            if (parent2000?.id && subId !== parent2000.id) {
              parentToSubLedgerMap[parent2000.id as string] = subId;
            }
          }
        }
      }
    } catch { /* fallback: use original account_id as-is */ }

    const reversalLines: JournalEntryLine[] = lines.map((line: any) => ({
      account_id: parentToSubLedgerMap[line.account_id] || line.account_id,
      debit: line.credit || 0,
      credit: line.debit || 0,
      description: line.description ? `Reversal: ${line.description}` : undefined,
    }));

    const reversalEntry: JournalEntry = {
      company_id: companyId,
      branch_id: branchId || undefined,
      entry_no: entryNo,
      entry_date: entryDate,
      description: defaultDescription,
      reference_type: 'correction_reversal',
      reference_id: originalJournalEntryId,
      created_by: createdBy || null,
      economic_event_id: chainPaymentId || undefined,
    };
    try {
      const result = await this.createEntry(
        reversalEntry,
        reversalLines,
        chainPaymentId || undefined
      );
      if (!result) return null;
      console.log('[PAYMENT_REVERSAL_TRACE]', JSON.stringify({ phase: 'reversal_je_created', originalJournalEntryId, reversalId: (result as any).id }));
      try {
        const pid = (((original as any).payment_id as string | null | undefined) || '').trim() || '';
        if (pid) {
          const { data: prow } = await supabase
            .from('payments')
            .select('id, reference_type, voided_at, contact_id')
            .eq('id', pid)
            .eq('company_id', companyId)
            .maybeSingle();
          const rt = String((prow as any)?.reference_type || '').toLowerCase();
          const canVoidRow = prow && !(prow as any).voided_at;
          const voidable =
            canVoidRow &&
            (rt === 'manual_receipt' || rt === 'on_account' || rt === 'manual_payment' || rt === 'sale' || rt === 'purchase');

          if (voidable) {
            const { voidPaymentAfterJournalReversal } = await import('@/app/services/paymentLifecycleService');
            await voidPaymentAfterJournalReversal({ companyId, paymentId: pid });
            console.log('[PAYMENT_REVERSAL_TRACE]', JSON.stringify({ phase: 'payment_voided', paymentId: pid, reference_type: rt }));
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('paymentAdded'));
              if (rt === 'manual_payment' || rt === 'purchase') {
                const cid = (prow as any).contact_id as string | null | undefined;
                window.dispatchEvent(new CustomEvent('ledgerUpdated', { detail: { ledgerType: 'supplier', entityId: cid || undefined } }));
              } else {
                window.dispatchEvent(new CustomEvent('ledgerUpdated', { detail: { ledgerType: 'customer' } }));
              }
            }
          }
        }
      } catch (voidErr: any) {
        console.warn('[ACCOUNTING] Reversal posted but linked payment void failed:', voidErr?.message || voidErr);
      }
      try {
        const { rentalService } = await import('@/app/services/rentalService');
        const rv = await rentalService.voidRentalPaymentByReversedJournal(companyId, originalJournalEntryId);
        if (rv && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('rentalPaymentsChanged'));
        }
      } catch (rentalVoidErr: any) {
        console.warn('[ACCOUNTING] Reversal posted but rental payment void failed:', rentalVoidErr?.message || rentalVoidErr);
      }
      try {
        const origRef = String((original as any).reference_type || '').toLowerCase();
        if (origRef === 'fx_currency_purchase') {
          const { finalizeFxCurrencyPurchaseCreditAfterReversal } = await import(
            '@/app/services/importFxAgentService'
          );
          await finalizeFxCurrencyPurchaseCreditAfterReversal({
            companyId,
            originalJournalEntryId,
            reversalJournalEntryId: String((result as any).id),
            reason: reason?.trim() || 'Path 21: FX credit journal reversed',
          });
        }
      } catch (fxVoidErr: any) {
        console.warn(
          '[ACCOUNTING] Reversal posted but Path 21 FX credit finalize failed:',
          fxVoidErr?.message || fxVoidErr
        );
      }
      return { id: (result as any).id, alreadyExisted: false };
    } catch (e: any) {
      const dup =
        e?.code === '23505' ||
        String(e?.message || '').toLowerCase().includes('duplicate') ||
        String(e?.message || '').toLowerCase().includes('unique');
      if (dup) {
        const again = await findActiveReversal();
        if (again) return { id: again, alreadyExisted: true };
      }
      throw e;
    }
  },

  // Get journal entry by reference number
  // Get journal entry by ID (PRIMARY METHOD - NO GUESSING)
  async getEntryById(journalEntryId: string, companyId: string) {
    if (!journalEntryId || !companyId) {
      console.error('[ACCOUNTING SERVICE] getEntryById: Missing journalEntryId or companyId');
      return null;
    }

    console.log('[ACCOUNTING SERVICE] getEntryById:', { journalEntryId, companyId });

    const { data, error } = await supabase
      .from('journal_entries')
      .select(`
        *,
        lines:journal_entry_lines(
          *,
          account:accounts(id, name, code, type)
        ),
        payment:payments(id, reference_number, reference_type, payment_type, notes, amount, payment_method, payment_date, contact_id, payment_account_id, attachments, contact:contacts(name)),
        branch:branches(id, name, code)
      `)
      .eq('id', journalEntryId)
      .eq('company_id', companyId)
      .maybeSingle();

    if (error) {
      console.error('[ACCOUNTING SERVICE] getEntryById error:', error);
      return null;
    }

    if (!data) {
      console.warn('[ACCOUNTING SERVICE] getEntryById: No entry found for ID:', journalEntryId);
      return null;
    }

    // CRITICAL FIX: Fetch sale data separately if reference_type is 'sale'
    if (data.reference_type === 'sale' && data.reference_id) {
      try {
        const { data: saleData, error: saleError } = await supabase
          .from('sales')
          .select('id, invoice_no, customer_name, total, paid_amount, due_amount, customer_id')
          .eq('id', data.reference_id)
          .single();
        
        if (!saleError && saleData) {
          (data as any).sale = saleData;
        }
      } catch (saleErr) {
        console.warn('[ACCOUNTING SERVICE] Could not fetch sale data:', saleErr);
      }
    }

    console.log('[ACCOUNTING SERVICE] getEntryById SUCCESS:', { entry_no: data.entry_no, id: data.id });
    return data;
  },

  /**
   * Load posted `journal_entry_lines` for one JE (authoritative for double-entry display).
   * Modal double-entry truth must come from posted journal_entry_lines for the selected JE, not merged payment transforms.
   */
  async getPostedJournalLinesForEntry(companyId: string, journalEntryId: string) {
    if (!companyId || !journalEntryId) return [];
    const { data: je, error: jeErr } = await supabase
      .from('journal_entries')
      .select('id')
      .eq('company_id', companyId)
      .eq('id', journalEntryId)
      .maybeSingle();
    if (jeErr || !je) return [];
    const { data: lines, error } = await supabase
      .from('journal_entry_lines')
      .select(`*, account:accounts(id, name, code, type)`)
      .eq('journal_entry_id', journalEntryId)
      .order('id', { ascending: true });
    if (error) {
      console.error('[ACCOUNTING SERVICE] getPostedJournalLinesForEntry:', error);
      return [];
    }
    return lines || [];
  },

  /**
   * Effective journal lines for a payment: original payment JE + any payment_adjustment JEs,
   * aggregated by account so the modal shows the correct accounts (e.g. Bank after edit from Cash).
   * If currentPaymentAccountId is provided and the stored JEs still show a different **liquidity** debit account
   * (customer receipt: Dr Bank was wrong ID until adjustment posts), returns synthetic lines with the current
   * payment account. Supplier payments (Dr AP, Cr Bank) must not use this path — see isLiquidityDebitForPaymentRemap.
   */
  async getEffectiveJournalLinesForPayment(
    paymentId: string,
    companyId: string,
    currentPaymentAccountId?: string | null
  ): Promise<{ id: string; account_id: string; account: { id: string; name: string; code?: string; type?: string }; debit: number; credit: number }[]> {
    if (!paymentId || !companyId) return [];
    const { data: entries, error } = await supabase
      .from('journal_entries')
      .select(`
        id,
        reference_type,
        is_void,
        lines:journal_entry_lines(
          id,
          account_id,
          debit,
          credit,
          account:accounts(id, name, code, type)
        )
      `)
      .eq('company_id', companyId)
      .or(`payment_id.eq.${paymentId},and(reference_type.eq.payment_adjustment,reference_id.eq.${paymentId})`);

    if (error || !entries?.length) return [];
    // PF-14.5B: Exclude voided JEs from effective lines
    const nonVoidEntries = (entries as any[]).filter((e: any) => e.is_void !== true);
    if (nonVoidEntries.length === 0) return [];

    const byAccount = new Map<string, { debit: number; credit: number; account: any }>();
    for (const je of nonVoidEntries) {
      const lines = (je as any).lines ?? [];
      for (const line of lines) {
        const acc = line.account ?? {};
        const aid = line.account_id ?? '';
        const debit = Number(line.debit ?? 0);
        const credit = Number(line.credit ?? 0);
        if (!byAccount.has(aid)) {
          byAccount.set(aid, { debit: 0, credit: 0, account: { id: acc.id, name: acc.name ?? 'Unknown', code: acc.code, type: acc.type } });
        }
        const row = byAccount.get(aid)!;
        row.debit += debit;
        row.credit += credit;
      }
    }

    let result = Array.from(byAccount.entries())
      .filter(([, row]) => row.debit > 0 || row.credit > 0)
      .map(([account_id, row]) => ({
        id: '',
        account_id,
        account: row.account,
        debit: Math.round(row.debit * 100) / 100,
        credit: Math.round(row.credit * 100) / 100,
      }));

    if (currentPaymentAccountId && result.length >= 2) {
      const debitAccountRow = result.find((r) => r.debit > 0);
      if (
        debitAccountRow &&
        debitAccountRow.account_id !== currentPaymentAccountId &&
        isLiquidityDebitForPaymentRemap(debitAccountRow.account)
      ) {
        const { data: accRow } = await supabase
          .from('accounts')
          .select('id, name, code, type')
          .eq('id', currentPaymentAccountId)
          .single();
        const name = (accRow as any)?.name ?? 'Account';
        const code = (accRow as any)?.code;
        result = [
          { id: '', account_id: currentPaymentAccountId, account: { id: currentPaymentAccountId, name, code, type: (accRow as any)?.type }, debit: debitAccountRow.debit, credit: 0 },
          ...result.filter((r) => r.credit > 0),
        ];
      }
    }
    return result;
  },

  // CRITICAL FIX: Lookup by entry_no, payment reference_number, or invoice_no (FALLBACK ONLY)
  async getEntryByReference(
    referenceNumber: string,
    companyId: string,
    options?: { journalEntryIdHint?: string }
  ) {
    if (!referenceNumber || !companyId) {
      console.error('[ACCOUNTING SERVICE] getEntryByReference: Missing referenceNumber or companyId');
      return null;
    }

    // CRITICAL: Don't force uppercase - preserve original case for exact match
    // But use uppercase for ilike search (case-insensitive)
    const cleanRef = referenceNumber.trim();
    const cleanRefUpper = cleanRef.toUpperCase();
    const hintJeId = String(options?.journalEntryIdHint || '').trim();
    
    let data: any = null;
    let error: any = null;

    const loadRentalPaymentJournalEntry = async (
      rentalJeId: string | null,
      rentalPaymentRef: string | null
    ): Promise<boolean> => {
      if (!rentalJeId) return false;
      const stepRp = await supabase
        .from('journal_entries')
        .select(`
          *,
          lines:journal_entry_lines(
            *,
            account:accounts(id, name, code, type)
          ),
          payment:payments(id, reference_number, reference_type, payment_type, notes, amount, payment_method, payment_date, contact_id, payment_account_id, attachments, contact:contacts(name)),
          branch:branches(id, name, code)
        `)
        .eq('company_id', companyId)
        .eq('id', rentalJeId)
        .limit(1);

      if (stepRp.error) {
        error = stepRp.error;
        return false;
      }
      data = pickPreferredJournalEntryRow(stepRp.data as any[]) ?? null;
      if (data) {
        (data as { rental_payment_ref?: string }).rental_payment_ref =
          rentalPaymentRef || formatRentalPaymentRef(cleanRef.replace(/-PAY$/i, '')) || cleanRef;
        error = null;
        return true;
      }
      error = { code: 'PGRST116' } as any;
      return false;
    };

    // STEP 0: Bare rental booking no (REN-0002) → linked payment journal (not revenue JE)
    if (/^REN-\d+$/i.test(cleanRef)) {
      error = null;
      const { data: rentalRow } = await supabase
        .from('rentals')
        .select('id, booking_no')
        .eq('company_id', companyId)
        .ilike('booking_no', cleanRef)
        .maybeSingle();

      if (rentalRow?.id) {
        let rentalJeId: string | null = null;
        let rentalPaymentRef: string | null = formatRentalPaymentRef(String(rentalRow.booking_no || cleanRef));

        if (hintJeId) {
          const { data: rpByHint } = await supabase
            .from('rental_payments')
            .select('id, journal_entry_id, reference')
            .eq('rental_id', rentalRow.id)
            .eq('journal_entry_id', hintJeId)
            .is('voided_at', null)
            .maybeSingle();
          if (rpByHint) {
            rentalJeId = (rpByHint as { journal_entry_id?: string | null }).journal_entry_id ?? null;
            rentalPaymentRef =
              String((rpByHint as { reference?: string }).reference || '').trim() || rentalPaymentRef;
          }
        }

        if (!rentalJeId) {
          const { data: rpRows } = await supabase
            .from('rental_payments')
            .select('id, journal_entry_id, reference')
            .eq('rental_id', rentalRow.id)
            .is('voided_at', null)
            .not('journal_entry_id', 'is', null)
            .order('payment_date', { ascending: false })
            .order('created_at', { ascending: false })
            .limit(1);
          const rpRow = (rpRows || [])[0] as { journal_entry_id?: string | null; reference?: string } | undefined;
          if (rpRow) {
            rentalJeId = rpRow.journal_entry_id ?? null;
            rentalPaymentRef = String(rpRow.reference || '').trim() || rentalPaymentRef;
          }
        }

        if (!rentalJeId) {
          const { data: rpRows } = await supabase
            .from('rental_payments')
            .select('id, journal_entry_id, reference')
            .eq('rental_id', rentalRow.id)
            .is('voided_at', null)
            .order('payment_date', { ascending: false })
            .order('created_at', { ascending: false })
            .limit(1);
          const rpRow = (rpRows || [])[0] as { journal_entry_id?: string | null; reference?: string } | undefined;
          if (rpRow) {
            rentalJeId = rpRow.journal_entry_id ?? null;
            rentalPaymentRef = String(rpRow.reference || '').trim() || rentalPaymentRef;
          }
        }

        await loadRentalPaymentJournalEntry(rentalJeId, rentalPaymentRef);
      }
    }

    // STEP 1: entry_no (case-insensitive). No maybeSingle — duplicate entry_no caused PostgREST / UI failures.
    const step1 = await supabase
      .from('journal_entries')
      .select(`
        *,
        lines:journal_entry_lines(
          *,
          account:accounts(id, name, code, type)
        ),
        payment:payments(id, reference_number, reference_type, payment_type, notes, amount, payment_method, payment_date, contact_id, payment_account_id, attachments, contact:contacts(name)),
        branch:branches(id, name, code)
      `)
      .eq('company_id', companyId)
      .ilike('entry_no', cleanRefUpper)
      .order('created_at', { ascending: true })
      .limit(25);
    if (step1.error) {
      error = step1.error;
    } else {
      data = pickPreferredJournalEntryRow(step1.data as any[]) ?? null;
      error = data ? null : ({ code: 'PGRST116' } as any);
    }

    // STEP 2: If not found by entry_no, try payment reference_number → primary JE (exclude payment_adjustment rows).
    if (!data && (!error || error.code === 'PGRST116')) {
      error = null;
      const { data: paymentData } = await supabase
        .from('payments')
        .select('id')
        .eq('company_id', companyId)
        .ilike('reference_number', cleanRef)
        .maybeSingle();

      if (paymentData) {
        const step2 = await supabase
          .from('journal_entries')
          .select(`
            *,
            lines:journal_entry_lines(
              *,
              account:accounts(id, name, code, type)
            ),
            payment:payments(id, reference_number, reference_type, payment_type, notes, amount, payment_method, payment_date, contact_id, payment_account_id, attachments, contact:contacts(name)),
            branch:branches(id, name, code)
          `)
          .eq('company_id', companyId)
          .eq('payment_id', paymentData.id)
          .neq('reference_type', 'payment_adjustment')
          .order('created_at', { ascending: true })
          .limit(25);

        if (step2.error) {
          error = step2.error;
        } else {
          data = pickPreferredJournalEntryRow(step2.data as any[]) ?? null;
          error = data ? null : ({ code: 'PGRST116' } as any);
        }
      }
    }

    // STEP 2.4: RCV-* on rental_payments → linked journal entry (rental receipts)
    if (!data && (!error || error.code === 'PGRST116') && isRcvReference(cleanRef)) {
      error = null;
      let rentalJeId: string | null = null;
      const rentalPaymentRef = cleanRef;

      const rpBaseQuery = () =>
        supabase
          .from('rental_payments')
          .select('id, journal_entry_id, reference, rentals!inner(company_id)')
          .eq('rentals.company_id', companyId)
          .ilike('reference', cleanRef)
          .is('voided_at', null)
          .order('created_at', { ascending: true });

      if (hintJeId) {
        const { data: rpByHint } = await rpBaseQuery().eq('journal_entry_id', hintJeId).limit(1).maybeSingle();
        if (rpByHint) {
          rentalJeId = (rpByHint as { journal_entry_id?: string | null }).journal_entry_id ?? null;
        }
      }

      if (!rentalJeId) {
        const { data: rpRows } = await rpBaseQuery().limit(1);
        const rpRow = (rpRows || [])[0] as { journal_entry_id?: string | null } | undefined;
        if (rpRow) rentalJeId = rpRow.journal_entry_id ?? null;
      }

      await loadRentalPaymentJournalEntry(rentalJeId, rentalPaymentRef);
    }

    // STEP 2.5: Rental payment voucher (REN-*-PAY) → linked journal entry
    if (!data && (!error || error.code === 'PGRST116') && /^REN-.+-PAY$/i.test(cleanRef)) {
      error = null;
      let rentalJeId: string | null = null;
      let rentalPaymentRef: string | null = null;

      const { data: rpByRef } = await supabase
        .from('rental_payments')
        .select('id, journal_entry_id, reference, rentals!inner(company_id, booking_no)')
        .eq('rentals.company_id', companyId)
        .ilike('reference', cleanRef)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (rpByRef) {
        rentalJeId = (rpByRef as { journal_entry_id?: string | null }).journal_entry_id ?? null;
        const rentalsJoin = (rpByRef as { rentals?: { booking_no?: string } | { booking_no?: string }[] }).rentals;
        const rentalMeta = Array.isArray(rentalsJoin) ? rentalsJoin[0] : rentalsJoin;
        rentalPaymentRef =
          String((rpByRef as { reference?: string }).reference || '').trim() ||
          formatRentalPaymentRef(String(rentalMeta?.booking_no || cleanRef.replace(/-PAY$/i, '')));
      } else {
        const bookingNo = cleanRef.replace(/-PAY$/i, '');
        const { data: rentalRow } = await supabase
          .from('rentals')
          .select('id, booking_no')
          .eq('company_id', companyId)
          .ilike('booking_no', bookingNo)
          .maybeSingle();
        if (rentalRow?.id) {
          const { data: rpRow } = await supabase
            .from('rental_payments')
            .select('id, journal_entry_id, reference')
            .eq('rental_id', rentalRow.id)
            .order('payment_date', { ascending: true })
            .order('created_at', { ascending: true })
            .limit(1)
            .maybeSingle();
          if (rpRow) {
            rentalJeId = (rpRow as { journal_entry_id?: string | null }).journal_entry_id ?? null;
            rentalPaymentRef =
              String((rpRow as { reference?: string }).reference || '').trim() ||
              formatRentalPaymentRef(String(rentalRow.booking_no || bookingNo));
          }
        }
      }

      await loadRentalPaymentJournalEntry(rentalJeId, rentalPaymentRef || cleanRef);
    }

    // STEP 3: If still not found, try invoice_no (for sales)
    if (!data && (!error || error.code === 'PGRST116')) {
      error = null;
      const { data: saleData } = await supabase
        .from('sales')
        .select('id')
        .eq('company_id', companyId)
        .ilike('invoice_no', cleanRef)
        .maybeSingle();

      if (saleData) {
        const step3 = await supabase
          .from('journal_entries')
          .select(`
            *,
            lines:journal_entry_lines(
              *,
              account:accounts(id, name, code, type)
            ),
          payment:payments(id, reference_number, reference_type, payment_type, notes, amount, payment_method, payment_date, contact_id, payment_account_id, attachments, contact:contacts(name)),
            branch:branches(id, name, code)
          `)
          .eq('company_id', companyId)
          .eq('reference_type', 'sale')
          .eq('reference_id', saleData.id)
          .order('created_at', { ascending: false })
          .limit(15);

        if (step3.error) {
          error = step3.error;
        } else {
          const jeData = pickLatestJournalEntryRow(step3.data as any[]);
          if (jeData) {
            data = jeData;
            error = null;
            if (jeData.reference_type === 'sale' && jeData.reference_id) {
              try {
                const { data: saleRow } = await supabase
                  .from('sales')
                  .select('id, invoice_no, customer_name, total, paid_amount, due_amount')
                  .eq('id', jeData.reference_id)
                  .single();
                if (saleRow) (data as any).sale = saleRow;
              } catch {}
            }
          } else {
            error = { code: 'PGRST116' } as any;
          }
        }
      }
    }

    // STEP 4: Exact entry_no (case-sensitive), duplicate-safe
    if (!data && (!error || error.code === 'PGRST116')) {
      error = null;
      const step4 = await supabase
        .from('journal_entries')
        .select(`
          *,
          lines:journal_entry_lines(
            *,
            account:accounts(id, name, code, type)
          ),
          payment:payments(id, reference_number, reference_type, payment_type, notes, amount, payment_method, payment_date, contact_id, payment_account_id, attachments, contact:contacts(name)),
          branch:branches(id, name, code)
        `)
        .eq('company_id', companyId)
        .eq('entry_no', referenceNumber.trim())
        .order('created_at', { ascending: true })
        .limit(25);

      if (step4.error) {
        error = step4.error;
      } else {
        data = pickPreferredJournalEntryRow(step4.data as any[]) ?? null;
        error = data ? null : ({ code: 'PGRST116' } as any);
      }
    }

    // STEP 5: Partial entry_no match (duplicate-safe; prefer non-void + oldest)
    if (!data && (!error || error.code === 'PGRST116')) {
      error = null;
      const step5 = await supabase
        .from('journal_entries')
        .select(`
          *,
          lines:journal_entry_lines(
            *,
            account:accounts(id, name, code, type)
          ),
          payment:payments(id, reference_number, reference_type, payment_type, notes, amount, payment_method, payment_date, contact_id, payment_account_id, attachments, contact:contacts(name)),
          branch:branches(id, name, code)
        `)
        .eq('company_id', companyId)
        .ilike('entry_no', '%' + cleanRefUpper + '%')
        .order('created_at', { ascending: true })
        .limit(25);

      if (step5.error) {
        error = step5.error;
      } else {
        data = pickPreferredJournalEntryRow(step5.data as any[]) ?? null;
        error = data ? null : ({ code: 'PGRST116' } as any);
      }
    }

    // Log if not found
    if (!data && error && error.code !== 'PGRST116') {
      console.error('[ACCOUNTING SERVICE] Error finding entry by reference:', error);
      console.error('[ACCOUNTING SERVICE] Reference searched:', cleanRef, 'Original:', referenceNumber);
    }

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    return data || null;
  },

  // Get account ledger entries
  async getAccountLedger(
    accountId: string | string[],
    companyId: string,
    startDate?: string,
    endDate?: string,
    branchId?: string,
    searchTerm?: string
  ): Promise<AccountLedgerEntry[]> {
    try {
      const accountIds = (Array.isArray(accountId) ? accountId : [accountId])
        .map((id) => String(id || '').trim())
        .filter(Boolean);
      if (accountIds.length === 0) return [];
      const accountIdSet = new Set(accountIds);

      // Get all journal entry lines for this account (or union of accounts for canonical TB drill),
      // scoped to this company via inner join. Sort by Date ASC, then ID ASC (as per requirements).
      let query = supabase
        .from('journal_entry_lines')
        .select(`
          *,
          account:accounts(name, code),
          journal_entry:journal_entries!inner(
            id,
            entry_no,
            entry_date,
            description,
            reference_type,
            reference_id,
            payment_id,
            action_fingerprint,
            economic_event_id,
            branch_id,
            created_by,
            created_at,
            company_id,
            is_void,
            branch:branches(id, name, code)
          )
        `)
        .in('account_id', accountIds)
        .eq('journal_entries.company_id', companyId)
        .order('created_at', { ascending: true });

      const { data: lines, error } = await query;

      if (error) {
        console.error('[ACCOUNTING SERVICE] Error fetching ledger:', error);
        return [];
      }

      if (!lines || lines.length === 0) {
        return [];
      }

      // Phase 7: Current balance from journal only (single source of truth). Exclude voided JEs.
      const currentBalance = (lines as any[]).reduce((sum: number, line: any) => {
        const entry = line.journal_entry;
        if (!entry || (entry as any).is_void === true) return sum;
        return sum + ((line.debit || 0) - (line.credit || 0));
      }, 0);

      // Get payment references for entries that need them (batch fetch)
      const paymentIds = lines
        .map((line: any) => line.journal_entry?.payment_id)
        .filter((id: string | undefined) => id) as string[];
      
      const paymentRefsMap = new Map<string, { referenceNumber: string; bankTraceId: string }>();
      if (paymentIds.length > 0) {
        const payments = await fetchInBatches(paymentIds, async (chunk) => {
          const { data, error } = await supabase
            .from('payments')
            .select('id, reference_number, notes')
            .in('id', chunk);
          if (error) throw error;
          return data || [];
        });
        payments.forEach((p: any) => {
          if (p.reference_number) {
            paymentRefsMap.set(p.id, {
              referenceNumber: String(p.reference_number),
              bankTraceId: extractBankTraceId(p.notes),
            });
          }
        });
      }

      const saleIds = [...new Set(
        (lines as any[])
          .map((line: any) => line.journal_entry)
          .filter((entry: any) => {
            const rt = String(entry?.reference_type || '').toLowerCase();
            return (
              entry?.reference_id &&
              (['sale', 'sale_reversal', 'sale_return', 'gl_correction'].includes(rt))
            );
          })
          .map((entry: any) => String(entry.reference_id))
      )];
      const purchaseIds = [...new Set(
        (lines as any[])
          .map((line: any) => line.journal_entry)
          .filter((entry: any) => String(entry?.reference_type || '').toLowerCase() === 'purchase' && entry?.reference_id)
          .map((entry: any) => String(entry.reference_id))
      )];
      const saleDocMap = new Map<string, string>();
      const saleStatusById = new Map<string, string>();
      const purchaseDocMap = new Map<string, string>();
      const saleBranchById = new Map<string, string>();
      const purchaseBranchById = new Map<string, string>();
      const rentalBranchByRefId = new Map<string, string>();
      const branchLabelById = new Map<string, string>();
      if (saleIds.length) {
        const sales = await fetchInBatches(saleIds, async (chunk) => {
          const { data, error } = await supabase
            .from('sales')
            .select('id, invoice_no, order_no, draft_no, branch_id, status')
            .in('id', chunk);
          if (error) throw error;
          return data || [];
        });
        sales.forEach((s: any) => {
          const doc = String(s.invoice_no || s.order_no || s.draft_no || '').trim();
          if (doc) saleDocMap.set(String(s.id), doc);
          if (s?.status) saleStatusById.set(String(s.id), String(s.status));
          if (s?.branch_id) saleBranchById.set(String(s.id), String(s.branch_id));
        });
      }
      if (purchaseIds.length) {
        const purchases = await fetchInBatches(purchaseIds, async (chunk) => {
          const { data, error } = await supabase
            .from('purchases')
            .select('id, po_no, order_no, draft_no, branch_id')
            .in('id', chunk);
          if (error) throw error;
          return data || [];
        });
        purchases.forEach((p: any) => {
          const doc = String(p.po_no || p.order_no || p.draft_no || '').trim();
          if (doc) purchaseDocMap.set(String(p.id), doc);
          if (p?.branch_id) purchaseBranchById.set(String(p.id), String(p.branch_id));
        });
      }

      const ledgerPaymentIds = [
        ...new Set(
          (lines as any[])
            .flatMap((line: any) => {
              const entry = line.journal_entry;
              if (!entry) return [];
              const ids: string[] = [];
              if (entry.payment_id) ids.push(String(entry.payment_id));
              if (String(entry.reference_type || '').toLowerCase() === 'payment' && entry.reference_id) {
                ids.push(String(entry.reference_id));
              }
              return ids;
            })
        ),
      ];
      const paymentMetaById = new Map<string, { voided_at: string | null; sale_id: string | null }>();
      const paymentLinkedSaleIds: string[] = [];
      if (ledgerPaymentIds.length) {
        const payRows = await fetchInBatches(ledgerPaymentIds, async (chunk) => {
          const { data, error } = await supabase
            .from('payments')
            .select('id, voided_at, reference_type, reference_id')
            .eq('company_id', companyId)
            .in('id', chunk);
          if (error) throw error;
          return data || [];
        });
        payRows.forEach((p: any) => {
          const saleId =
            String(p.reference_type || '').toLowerCase() === 'sale' && p.reference_id
              ? String(p.reference_id)
              : null;
          paymentMetaById.set(String(p.id), {
            voided_at: p.voided_at ?? null,
            sale_id: saleId,
          });
          if (saleId && !saleStatusById.has(saleId)) paymentLinkedSaleIds.push(saleId);
        });
        if (paymentLinkedSaleIds.length) {
          const extraSales = await fetchInBatches([...new Set(paymentLinkedSaleIds)], async (chunk) => {
            const { data, error } = await supabase
              .from('sales')
              .select('id, invoice_no, order_no, draft_no, branch_id, status')
              .in('id', chunk);
            if (error) throw error;
            return data || [];
          });
          extraSales.forEach((s: any) => {
            const doc = String(s.invoice_no || s.order_no || s.draft_no || '').trim();
            if (doc) saleDocMap.set(String(s.id), doc);
            if (s?.status) saleStatusById.set(String(s.id), String(s.status));
            if (s?.branch_id) saleBranchById.set(String(s.id), String(s.branch_id));
          });
        }
      }

      const rentalRefIds = [
        ...new Set(
          (lines as any[])
            .map((line: any) => line.journal_entry)
            .filter((entry: any) => String(entry?.reference_type || '').toLowerCase() === 'rental' && entry?.reference_id)
            .map((entry: any) => String(entry.reference_id))
        ),
      ];
      if (rentalRefIds.length) {
        const rentalRows = await fetchInBatches(rentalRefIds, async (chunk) => {
          const { data, error } = await supabase.from('rentals').select('id, branch_id').in('id', chunk);
          if (error) throw error;
          return data || [];
        });
        rentalRows.forEach((r: any) => {
          if (r?.id && r.branch_id) rentalBranchByRefId.set(String(r.id), String(r.branch_id));
        });
      }
      const docBranchIds = [
        ...new Set([
          ...saleBranchById.values(),
          ...purchaseBranchById.values(),
          ...rentalBranchByRefId.values(),
        ]),
      ];
      if (docBranchIds.length) {
        const branchRows = await fetchInBatches(docBranchIds, async (chunk) => {
          const { data, error } = await supabase.from('branches').select('id, name, code').in('id', chunk);
          if (error) throw error;
          return data || [];
        });
        branchRows.forEach((b: any) => {
          if (!b?.id) return;
          const label = b.code ? `${b.code} | ${b.name}` : String(b.name || '');
          branchLabelById.set(String(b.id), label);
        });
      }

      // Build ledger entries with running balance
      // First filter, then sort by Date ASC, ID ASC (as per requirements)
      // PF-14.5B: Exclude voided JEs from business ledger
      const filteredLines = lines.filter((line: any) => {
        const entry = line.journal_entry;
        if (!entry) return false;
        if ((entry as any).is_void === true) return false;
        
        const entryDate = entry.entry_date;
        if (startDate && entryDate < startDate) return false;
        if (endDate && entryDate > endDate) return false;
        
        // Include NULL branch_id JEs when a branch is selected (company-wide openings).
        if (branchId && entry.branch_id != null && entry.branch_id !== '' && entry.branch_id !== branchId) {
          return false;
        }
        
        // Apply search filter if provided
        if (searchTerm && searchTerm.trim()) {
          const search = searchTerm.toLowerCase().trim();
          const entryNo = entry.entry_no?.toLowerCase() || '';
          const description = mergeLedgerEntryDescriptionForStatement(
            entry.reference_type,
            entry.description,
            line.description
          ).toLowerCase();
          const amount = ((line.debit || 0) + (line.credit || 0)).toString();
          
          if (!entryNo.includes(search) && 
              !description.includes(search) && 
              !amount.includes(search)) {
            return false;
          }
        }
        
        return true;
      });

      // Sort by posting date ASC, then created_at (time) ASC, then JE id (stable)
      filteredLines.sort((a: any, b: any) => {
        const dateA = new Date(a.journal_entry?.entry_date || 0).getTime();
        const dateB = new Date(b.journal_entry?.entry_date || 0).getTime();
        if (dateA !== dateB) return dateA - dateB;
        const tA = a.journal_entry?.created_at ? new Date(a.journal_entry.created_at).getTime() : 0;
        const tB = b.journal_entry?.created_at ? new Date(b.journal_entry.created_at).getTime() : 0;
        if (tA !== tB) return tA - tB;
        const idA = a.journal_entry?.id || '';
        const idB = b.journal_entry?.id || '';
        return idA.localeCompare(idB);
      });

      // Fetch counter-account names (other side of double-entry) for each journal entry
      const jeIds = [...new Set(filteredLines.map((l: any) => l.journal_entry?.id).filter(Boolean))] as string[];
      const counterAccountMap = new Map<string, string>();
      if (jeIds.length > 0) {
        const otherLines = await fetchInBatches(jeIds, async (chunk) => {
          const { data, error } = await supabase
            .from('journal_entry_lines')
            .select('journal_entry_id, account_id, account:accounts(name)')
            .in('journal_entry_id', chunk);
          if (error) throw error;
          return (data || []).filter((ol: any) => !accountIdSet.has(String(ol.account_id || '')));
        });
        for (const ol of otherLines) {
          const name = (ol as any).account?.name ?? 'Unknown';
          const arr = counterAccountMap.get(ol.journal_entry_id)
            ? `${counterAccountMap.get(ol.journal_entry_id)}, ${name}`
            : name;
          counterAccountMap.set(ol.journal_entry_id, arr);
        }
      }

      // Reversal rows: resolve original JE entry_no for clear pairing (avoids "duplicate" confusion)
      const reversalTargetIds = [
        ...new Set(
          filteredLines
            .map((l: any) => l.journal_entry)
            .filter(
              (e: any) =>
                e && String(e.reference_type || '').toLowerCase() === 'correction_reversal' && e.reference_id
            )
            .map((e: any) => e.reference_id as string)
        ),
      ];
      const reversedEntryNoByJeId = new Map<string, string>();
      if (reversalTargetIds.length > 0) {
        const origJes = await fetchInBatches(reversalTargetIds, async (chunk) => {
          const { data, error } = await supabase
            .from('journal_entries')
            .select('id, entry_no')
            .eq('company_id', companyId)
            .in('id', chunk);
          if (error) throw error;
          return data || [];
        });
        origJes.forEach((r: any) => {
          if (r?.id) reversedEntryNoByJeId.set(r.id, String(r.entry_no || '').trim() || r.id.slice(0, 8));
        });
      }

      // Expense documents: show EXP-no · JE-no so two postings for same expense are distinguishable
      const expenseDocIds = [
        ...new Set(
          filteredLines
            .map((l: any) => l.journal_entry)
            .filter((e: any) => {
              const rt = String(e?.reference_type || '').toLowerCase();
              return e?.reference_id && (rt === 'expense' || rt === 'extra_expense');
            })
            .map((e: any) => e.reference_id as string)
        ),
      ];
      const expenseNoById = new Map<string, string>();
      if (expenseDocIds.length > 0) {
        const { data: exRows } = await supabase
          .from('expenses')
          .select('id, expense_no')
          .eq('company_id', companyId)
          .in('id', expenseDocIds);
        (exRows || []).forEach((r: any) => {
          if (r?.id && r.expense_no) expenseNoById.set(r.id, String(r.expense_no).trim());
        });
      }

      // Opening balance = current balance minus sum of movements in this range (so running balance is correct for each row)
      const totalMovementInRange = filteredLines.reduce(
        (sum: number, line: any) => sum + ((line.debit || 0) - (line.credit || 0)),
        0
      );
      let runningBalance = currentBalance - totalMovementInRange;

      const isUuidOrBad = (s: string | null | undefined) => {
        if (!s || !s.trim()) return true;
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s.trim())) return true;
        return false;
      };

      const ledgerEntries: AccountLedgerEntry[] = filteredLines.map((line: any) => {
          const entry = line.journal_entry;
          runningBalance += (line.debit || 0) - (line.credit || 0);

          const refType = String(entry.reference_type || '').toLowerCase();
          let ledgerKind: 'standard' | 'reversal' | undefined;

          let sourceModule = 'Accounting';
          if (refType === 'sale') {
            sourceModule = 'Sales';
          } else if (refType === 'sale_reversal') {
            sourceModule = 'Sales';
            ledgerKind = 'reversal';
          } else if (refType === 'correction_reversal') {
            sourceModule = 'Reversal';
          } else if (entry.payment_id) {
            sourceModule = 'Payment';
          }

          let referenceNumber = entry.entry_no;
          const isUUID = referenceNumber && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(referenceNumber);
          const isShortFormat = referenceNumber && /^[A-Z]+-[0-9]+$/.test(referenceNumber);

          if (refType === 'correction_reversal') {
            ledgerKind = 'reversal';
            const origNo = entry.reference_id ? reversedEntryNoByJeId.get(entry.reference_id) : '';
            const selfJe =
              entry.entry_no && /^[A-Z]+-[0-9]+$/i.test(String(entry.entry_no))
                ? String(entry.entry_no)
                : `JE-${String(entry.id).slice(0, 4).toUpperCase()}`;
            referenceNumber = origNo ? `REV of ${origNo}` : `${selfJe} (reversal)`;
          } else if (!referenceNumber || referenceNumber.length > 20 || isUUID || !isShortFormat) {
            if (refType === 'sale' && entry.reference_id && saleDocMap.has(entry.reference_id)) {
              referenceNumber = saleDocMap.get(entry.reference_id)!;
            } else if (refType === 'purchase' && entry.reference_id && purchaseDocMap.has(entry.reference_id)) {
              referenceNumber = purchaseDocMap.get(entry.reference_id)!;
            } else if ((refType === 'manual_receipt' || refType === 'manual_payment') && entry.payment_id && paymentRefsMap.has(entry.payment_id)) {
              const pay = paymentRefsMap.get(entry.payment_id)!;
              referenceNumber = pay.bankTraceId || pay.referenceNumber;
            } else if (entry.payment_id && paymentRefsMap.has(entry.payment_id)) {
              referenceNumber = paymentRefsMap.get(entry.payment_id)!.referenceNumber;
            } else if (refType === 'expense' || refType === 'extra_expense') {
              referenceNumber = `EXP-${entry.id.substring(0, 4).toUpperCase()}`;
            } else {
              referenceNumber = `JE-${entry.id.substring(0, 4).toUpperCase()}`;
            }
          }

          if ((refType === 'expense' || refType === 'extra_expense') && entry.reference_id) {
            const expNo = expenseNoById.get(entry.reference_id);
            const jeNo =
              entry.entry_no && /^[A-Z]+-[0-9]+$/i.test(String(entry.entry_no)) ? String(entry.entry_no) : null;
            if (expNo && jeNo) {
              referenceNumber = `${expNo} · ${jeNo}`;
            } else if (expNo) {
              referenceNumber = expNo;
            }
          }

          let description = mergeLedgerEntryDescriptionForStatement(entry.reference_type, entry.description, line.description);
          if (refType === 'correction_reversal') {
            const origNo = entry.reference_id ? reversedEntryNoByJeId.get(entry.reference_id) : '';
            const base = description.trim();
            if (origNo) {
              description = base.toLowerCase().includes('reversal of')
                ? base
                : `Reversal of ${origNo}${base ? ` — ${base}` : ''}`;
            } else if (isUuidOrBad(description)) {
              description = `Reversal ${referenceNumber}`;
            }
          } else if (isUuidOrBad(description)) {
            if (refType === 'payment') description = `Payment ${referenceNumber}`;
            else if (refType === 'sale' || refType === 'sale_adjustment') description = `Sale ${referenceNumber}`;
            else if (refType === 'purchase') description = `Purchase ${referenceNumber}`;
            else if (refType === 'expense' || refType === 'extra_expense') description = `Expense ${referenceNumber}`;
            else if (refType === 'rental') description = `Rental ${referenceNumber}`;
            else description = `Journal entry ${referenceNumber}`;
          }

          const branch = (entry as any).branch;
          let branchName = branch ? (branch.code ? `${branch.code} | ${branch.name}` : branch.name) : null;
          let resolvedBranchId = entry.branch_id as string | null | undefined;
          if (!resolvedBranchId) {
            if (refType === 'rental' && entry.reference_id) {
              resolvedBranchId = rentalBranchByRefId.get(String(entry.reference_id));
            } else if (refType === 'sale' && entry.reference_id) {
              resolvedBranchId = saleBranchById.get(String(entry.reference_id));
            } else if (refType === 'purchase' && entry.reference_id) {
              resolvedBranchId = purchaseBranchById.get(String(entry.reference_id));
            }
          }
          if (!branchName && resolvedBranchId) {
            branchName = branchLabelById.get(String(resolvedBranchId)) ?? null;
          }
          const counterAccount = counterAccountMap.get(entry.id) || null;
          const acc = (line as { account?: { name?: string; code?: string } | null }).account;
          const accName = acc?.name ? String(acc.name) : '';
          const accCode = acc?.code != null && String(acc.code).trim() !== '' ? String(acc.code).trim() : '';
          const accountNameLine = accName ? (accCode ? `${accName} (${accCode})` : accName) : undefined;

          const linkedSaleId =
            entry.reference_id &&
            ['sale', 'sale_reversal', 'sale_return', 'gl_correction'].includes(refType)
              ? String(entry.reference_id)
              : undefined;

          const payMeta =
            (entry.payment_id && paymentMetaById.get(String(entry.payment_id))) ||
            (refType === 'payment' && entry.reference_id
              ? paymentMetaById.get(String(entry.reference_id))
              : undefined);
          const paymentSaleId = payMeta?.sale_id ?? null;
          const resolvedLinkedSaleId = linkedSaleId ?? paymentSaleId ?? undefined;

          return {
            date: entry.entry_date,
            created_at: (entry as any).created_at,
            reference_number: referenceNumber,
            entry_no: entry.entry_no,
            description,
            debit: line.debit || 0,
            credit: line.credit || 0,
            running_balance: runningBalance,
            source_module: sourceModule,
            created_by: entry.created_by,
            journal_entry_id: entry.id,
            je_reference_type: refType,
            je_action_fingerprint: (entry as { action_fingerprint?: string | null }).action_fingerprint ?? null,
            journal_line_id: line.id != null ? String(line.id) : null,
            je_reference_id: entry.reference_id != null ? String(entry.reference_id) : null,
            payment_id: entry.payment_id,
            sale_id: resolvedLinkedSaleId ?? entry.reference_id,
            linked_sale_status: resolvedLinkedSaleId
              ? saleStatusById.get(resolvedLinkedSaleId) ?? null
              : null,
            payment_voided_at: payMeta?.voided_at ?? null,
            branch_id: resolvedBranchId ?? entry.branch_id,
            branch_name: branchName,
            account_name: accountNameLine,
            gl_account_code: accCode || undefined,
            counter_account: counterAccount ?? undefined,
            ledger_kind: ledgerKind,
            document_type: glStatementDocumentTypeFromReferenceType(refType, sourceModule),
            economic_event_id: (entry as { economic_event_id?: string | null }).economic_event_id ?? null,
          };
        });

      // PHASE 5: LOG FINAL RESULTS
      console.log('[ACCOUNTING SERVICE] getCustomerLedger - FINAL RESULT:', {
        totalEntries: ledgerEntries.length,
        totalDebit: ledgerEntries.reduce((sum, e) => sum + (e.debit || 0), 0),
        totalCredit: ledgerEntries.reduce((sum, e) => sum + (e.credit || 0), 0),
        sampleEntries: ledgerEntries.slice(0, 3).map(e => ({
          reference: e.reference_number,
          debit: e.debit,
          credit: e.credit,
          journal_entry_id: e.journal_entry_id
        }))
      });

      return ledgerEntries;
    } catch (error: any) {
      console.error('[ACCOUNTING SERVICE] Error getting account ledger:', error);
      return [];
    }
  },

  // Get account transactions (simplified view)
  async getAccountTransactions(accountId: string, companyId: string) {
    const ledger = await this.getAccountLedger(accountId, companyId);
    return ledger.map(entry => ({
      date: entry.date,
      reference: entry.reference_number,
      description: entry.description,
      amount: entry.debit > 0 ? entry.debit : -entry.credit,
      type: entry.debit > 0 ? 'Debit' : 'Credit',
      balance: entry.running_balance,
    }));
  },

  // Get customer ledger from journal_entries (filtered by Accounts Receivable account)
  /**
   * @param ledgerMode `gl_journal_only` — AR journal lines for this customer only; no document/RPC merge into running balance.
   */
  async getCustomerLedger(
    customerId: string,
    companyId: string,
    branchId?: string,
    startDate?: string,
    endDate?: string,
    searchTerm?: string,
    ledgerMode: 'default' | 'gl_journal_only' = 'default'
  ): Promise<AccountLedgerEntry[]> {
    const glJournalOnly = ledgerMode === 'gl_journal_only';
    try {
      let customerDisplayName = '';
      const { data: customerContactRow } = await supabase
        .from('contacts')
        .select('name')
        .eq('id', customerId)
        .maybeSingle();
      customerDisplayName = String((customerContactRow as { name?: string } | null)?.name || '').trim();

      // Accounts Receivable: control 1100 **and all descendants** (per-contact Receivable — … subledgers).
      // Old query only matched code 1100 or name ilike %Accounts Receivable%; party AR children are named
      // "Receivable — {name}" and were excluded — sales/returns on subledgers never appeared in customer ledger.
      const { data: accountRows } = await supabase
        .from('accounts')
        .select('id, parent_id, code, name')
        .eq('company_id', companyId)
        .eq('is_active', true);

      const rows = (accountRows || []) as { id: string; parent_id?: string | null; code?: string; name?: string }[];
      const rootAr =
        rows.find((a) => String(a.code || '').trim() === '1100') ||
        rows.find((a) => /^accounts\s*receivable$/i.test(String(a.name || '').trim())) ||
        rows.find((a) => /accounts\s+receivable/i.test(String(a.name || '')));

      let arAccountIds: string[] = [];
      if (rootAr) {
        arAccountIds = collectDescendantAccountIds(
          rows.map((r) => ({ id: r.id, parent_id: r.parent_id })),
          String(rootAr.id)
        );
      }
      if (arAccountIds.length === 0) {
        const { data: rawAr } = await supabase
          .from('accounts')
          .select('id, code, name')
          .eq('company_id', companyId)
          .eq('is_active', true)
          .or('code.eq.1100,name.ilike.%Accounts Receivable%');
        const fallback = (rawAr || []).filter((a: any) => String(a.code || '').trim() !== '2000');
        arAccountIds = fallback.map((a: any) => a.id);
      }
      if (arAccountIds.length === 0) {
        console.warn('[ACCOUNTING SERVICE] Accounts Receivable account not found');
        return [];
      }

      console.log('[ACCOUNTING SERVICE] getCustomerLedger - AR account id count (1100 subtree):', arAccountIds.length);

      console.log('[ACCOUNTING SERVICE] getCustomerLedger - PHASE 1: Starting with customerId:', customerId);

      const arLineSelect = `
          *,
          account:accounts(id, name, code),
          journal_entry:journal_entries(
            id,
            entry_no,
            entry_date,
            description,
            reference_type,
            reference_id,
            payment_id,
            action_fingerprint,
            branch_id,
            created_by,
            created_at,
            is_void,
            branch:branches(id, name, code)
          )
        `;
      let lines: any[] = [];
      let error: { message?: string } | null = null;
      try {
        lines = await fetchInBatches(arAccountIds, async (chunk) => {
          const { data, error: chunkErr } = await supabase
            .from('journal_entry_lines')
            .select(arLineSelect)
            .in('account_id', chunk)
            .order('created_at', { ascending: true });
          if (chunkErr) throw chunkErr;
          return data || [];
        });
        lines.sort((a: any, b: any) => {
          const tA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const tB = b.created_at ? new Date(b.created_at).getTime() : 0;
          return tA - tB;
        });
      } catch (e: any) {
        error = e;
        lines = [];
      }
      
      console.log('[ACCOUNTING SERVICE] getCustomerLedger - PHASE 1: Total AR journal entry lines:', lines?.length || 0);

      // RPC-first: journal error par bhi return [] mat karo – RPC + synthetic se ledger bharo
      if (error) {
        console.error('[ACCOUNTING SERVICE] Error fetching journal lines (continuing with RPC/synthetic):', error);
      }
      // PF-14.4: Exclude lines from voided JEs (business ledger must not show them).
      const linesToUse = (lines || []).filter((l: any) => (l.journal_entry && (l.journal_entry as any).is_void) !== true);

      // PHASE 2: Get ALL sales and payments for this customer (RPC – always run so we can show ledger when no journal entries)
      // Use RPC get_customer_ledger_sales so we get sales regardless of branch (SECURITY DEFINER bypasses RLS)
      console.log('[ACCOUNTING SERVICE] getCustomerLedger - PHASE 2: Fetching sales via RPC', { customerId, companyId });
      
      // All final sales for party (same reader as customer statement); date scope for synthetic rows applied below.
      const customerSales = await fetchCustomerLedgerSalesForRange(
        companyId,
        customerId,
        undefined,
        undefined,
        ledgerSalesRpcBranchId(branchId)
      );
      const saleIds = customerSales.map((s: any) => s.id);
      console.log('[ACCOUNTING SERVICE] getCustomerLedger - Customer sales (canonical fetch):', saleIds.length);
      
      // Received payments: includes advances on non-final sales/orders (not gated by final saleIds RPC).
      let customerPayments: any[] = glJournalOnly
        ? []
        : await fetchCustomerReceivedPaymentsForRange(
            companyId,
            customerId,
            startDate,
            endDate,
            'live',
            ledgerSalesRpcBranchId(branchId)
          );

      const { data: allCustomerSalesMeta } = await supabase
        .from('sales')
        .select('id, status, invoice_no')
        .eq('company_id', companyId)
        .eq('customer_id', customerId);
      const saleStatusById = new Map<string, string>();
      (allCustomerSalesMeta || []).forEach((s: any) => {
        if (s?.id) saleStatusById.set(String(s.id), String(s.status || ''));
      });
      
      const accountMap = new Map<string, string>();

      console.log('[ACCOUNTING SERVICE] getCustomerLedger - Payments found (RPC):', customerPayments.length);
      if (customerPayments && customerPayments.length > 0) {
        console.log('[ACCOUNTING SERVICE] getCustomerLedger - Sample payment:', {
          id: customerPayments[0].id,
          reference_id: customerPayments[0].reference_id,
          reference_number: customerPayments[0].reference_number
        });
      }

      const paymentIds = customerPayments?.map(p => p.id) || [];
      const paymentRefsMap = new Map<string, { referenceNumber: string; bankTraceId: string }>();
      const paymentDetailsMap = new Map<string, any>();
      customerPayments?.forEach((p: any) => {
        if (p.reference_number) {
          paymentRefsMap.set(p.id, {
            referenceNumber: String(p.reference_number),
            bankTraceId: extractBankTraceId(p.notes),
          });
        }
        paymentDetailsMap.set(p.id, p);
      });
      
      console.log('[ACCOUNTING SERVICE] getCustomerLedger - Payments found:', paymentIds.length);

      // Sales already from RPC above (customerSales). Build salesMap for journal entry matching.
      const salesMap = new Map();
      customerSales.forEach((sale: any) => {
        salesMap.set(sale.id, {
          id: sale.id,
          invoice_no: sale.invoice_no,
          customer_id: customerId,
          branch_id: sale.branch_id ?? null,
        });
      });
      // Also include cancelled sales in salesMap (for sale_reversal JE matching only — no synthetic rows)
      const { data: cancelledSales } = await supabase
        .from('sales')
        .select('id, invoice_no')
        .eq('company_id', companyId)
        .eq('customer_id', customerId)
        .eq('status', 'cancelled');
      if (cancelledSales?.length) {
        for (const cs of cancelledSales) {
          if (!salesMap.has(cs.id)) {
            salesMap.set(cs.id, { id: cs.id, invoice_no: cs.invoice_no, customer_id: customerId });
          }
        }
      }
      console.log('[ACCOUNTING SERVICE] getCustomerLedger - Sales map size:', salesMap.size);

      const customerSaleReturnIds = await buildCustomerSaleReturnIdSet(companyId, customerId, salesMap);
      console.log('[ACCOUNTING SERVICE] getCustomerLedger - Sale return ids for party:', customerSaleReturnIds.size);

      // PHASE 2b: Get rentals and rental_payments for this customer (for customer sub-ledger)
      // Pass null dates to RPC so we get ALL rentals; date filtering happens in merge
      let customerRentals: any[] = [];
      let customerRentalPayments: any[] = [];
      try {
        const rpcRentals = await supabase.rpc('get_customer_ledger_rentals', {
          p_company_id: companyId,
          p_customer_id: customerId,
          p_from_date: null,
          p_to_date: null,
        });
        if (!rpcRentals.error) {
          customerRentals = rpcRentals.data ?? [];
        } else {
          // Fallback: direct query if RPC missing
          const { data: directRentals } = await supabase
            .from('rentals')
            .select('id, booking_no, booking_date, pickup_date, return_date, total_amount, paid_amount, due_amount, status, created_at, branch_id')
            .eq('company_id', companyId)
            .eq('customer_id', customerId);
          customerRentals = directRentals ?? [];
        }
        const rentalIds = customerRentals.map((r: any) => r.id);
        if (rentalIds.length > 0) {
          const { data: rpData } = await supabase
            .from('rental_payments')
            .select('id, rental_id, amount, method, payment_date, created_at, journal_entry_id, reference, voided_at, payment_type')
            .in('rental_id', rentalIds)
            .is('voided_at', null);
          customerRentalPayments = rpData ?? [];
        }
      } catch (e) {
        console.warn('[ACCOUNTING SERVICE] getCustomerLedger - Rental fetch failed (non-critical):', e);
      }
      if (customerRentals.length > 0) {
        const rentalIdsForDamage = customerRentals.map((r: any) => r.id).filter(Boolean);
        const { data: damageRows } = await supabase
          .from('rentals')
          .select('id, damage_charges')
          .in('id', rentalIdsForDamage);
        const damageById = new Map((damageRows || []).map((r: any) => [String(r.id), Number(r.damage_charges) || 0]));
        customerRentals = customerRentals.map((r: any) => ({
          ...r,
          damage_charges: r.damage_charges ?? damageById.get(String(r.id)) ?? 0,
        }));
      }
      const rentalsMap = new Map(customerRentals.map((r: any) => [r.id, r]));
      const rentalPaymentRefByJeId = new Map<string, string>();
      customerRentalPayments.forEach((rp: any) => {
        const jeId = String(rp.journal_entry_id || '').trim();
        const ref = String(rp.reference || '').trim();
        if (jeId && ref) rentalPaymentRefByJeId.set(jeId, ref);
      });
      const branchNameById = new Map<string, string>();
      const rentalBranchIds = [
        ...new Set(customerRentals.map((r: any) => r.branch_id).filter(Boolean)),
      ] as string[];
      const saleBranchIds = [
        ...new Set(
          [...salesMap.values()]
            .map((s: any) => s.branch_id)
            .filter(Boolean)
        ),
      ] as string[];
      const statementBranchIds = [...new Set([...rentalBranchIds, ...saleBranchIds])];
      if (statementBranchIds.length > 0) {
        const { data: branchRows } = await supabase
          .from('branches')
          .select('id, name, code')
          .in('id', statementBranchIds);
        (branchRows || []).forEach((b: any) => {
          if (!b?.id) return;
          const label = b.code ? `${b.code} | ${b.name}` : String(b.name || '');
          branchNameById.set(String(b.id), label);
        });
      }
      const rentalLedgerBranch = (rentalId?: string) => {
        if (!rentalId) return { branch_id: undefined as string | undefined, branch_name: null as string | null };
        const rental = rentalsMap.get(rentalId);
        const bid = rental?.branch_id != null ? String(rental.branch_id) : undefined;
        return {
          branch_id: bid,
          branch_name: bid ? branchNameById.get(bid) ?? null : null,
        };
      };
      const rentalIdsForParty = new Set(
        customerRentals.map((r: any) => (r?.id != null ? String(r.id) : '')).filter(Boolean)
      );
      console.log('[ACCOUNTING SERVICE] getCustomerLedger - Rentals:', customerRentals.length, 'Payments:', customerRentalPayments.length);

      // PHASE 3: Filter by customer ID (from sales.customer_id OR payments via sales OR rentals)
      // MUST include BOTH sales (debit) AND payments (credit) entries
      console.log('[ACCOUNTING SERVICE] getCustomerLedger - PHASE 3: Filtering lines', {
        totalLines: linesToUse.length,
        paymentIds: paymentIds.length,
        customerSalesCount: salesMap.size,
        customerId,
        salesMapKeys: Array.from(salesMap.keys()).slice(0, 5)
      });

      let saleMatchCount = 0;
      let paymentMatchCount = 0;

      const customerLines = linesToUse.filter((line: any) => {
        const entry = line.journal_entry;
        if (!entry) return false;

        // PF-07: correction_reversal JEs reference the original JE id — include AR lines when the original belonged to this customer
        if (entry.reference_type === 'correction_reversal' && entry.reference_id) {
          const origId = String(entry.reference_id);
          return linesToUse.some((ol: any) => {
            const oe = ol.journal_entry;
            if (!oe || String(oe.id) !== origId) return false;
            return arJournalLineMatchesCustomer(
              ol,
              customerId,
              salesMap,
              paymentIds,
              paymentDetailsMap,
              customerSaleReturnIds,
              rentalIdsForParty
            );
          });
        }

        const ok = arJournalLineMatchesCustomer(
          line,
          customerId,
          salesMap,
          paymentIds,
          paymentDetailsMap,
          customerSaleReturnIds,
          rentalIdsForParty
        );
        if (ok && entry.reference_type === 'sale') saleMatchCount++;
        if (ok && (entry.reference_type === 'payment' || entry.payment_id)) paymentMatchCount++;
        return ok;
      });

      console.log('[ACCOUNTING SERVICE] getCustomerLedger - PHASE 3: Filter results:', {
        saleMatches: saleMatchCount,
        paymentMatches: paymentMatchCount,
        totalCustomerLines: customerLines.length,
        sampleNoMatches: linesToUse.filter((l: any) => {
          const e = l.journal_entry;
          if (!e) return false;
          if (e.reference_type === 'sale' && e.reference_id && !salesMap.get(e.reference_id)) return true;
          return false;
        }).slice(0, 3).map((l: any) => ({
          entry_no: l.journal_entry?.entry_no,
          ref_type: l.journal_entry?.reference_type,
          ref_id: l.journal_entry?.reference_id
        }))
      });

      // Filter by branch: keep company-wide (NULL branch) JEs in every branch view
      const branchFiltered = branchId
        ? customerLines.filter((line: any) => {
            const bid = line.journal_entry?.branch_id;
            return bid == null || bid === '' || bid === branchId;
          })
        : customerLines;

      const compareJournalLines = (a: any, b: any) => {
        const dateA = (a.journal_entry?.entry_date || '').toString();
        const dateB = (b.journal_entry?.entry_date || '').toString();
        if (dateA !== dateB) return dateA.localeCompare(dateB);
        const createdA = (a.journal_entry?.created_at || a.created_at || '').toString();
        const createdB = (b.journal_entry?.created_at || b.created_at || '').toString();
        return createdA.localeCompare(createdB);
      };

      const dedupedBranch = dedupeCustomerArPaymentCreditLines([...branchFiltered].sort(compareJournalLines));

      // Opening balance = sum of (debit - credit) for ALL lines BEFORE startDate (after payment-line dedupe)
      let openingBalance = 0;
      if (startDate) {
        dedupedBranch.forEach((line: any) => {
          const entry = line.journal_entry;
          if (!entry || entry.entry_date >= startDate) return;
          openingBalance += (line.debit || 0) - (line.credit || 0);
        });
      }

      // Lines to display: only those within date range [startDate, endDate] (already sorted)
      const rangeLines = dedupedBranch.filter((line: any) => {
        const entry = line.journal_entry;
        if (!entry) return false;
        const entryDate = entry.entry_date;
        if (startDate && entryDate < startDate) return false;
        if (endDate && entryDate > endDate) return false;
        return true;
      });

      let runningBalance = openingBalance;

      const journalPaymentIds = [
        ...new Set(
          rangeLines
            .flatMap((l: any) => {
              const e = l.journal_entry;
              if (!e) return [];
              const ids: string[] = [];
              if (e.payment_id) ids.push(String(e.payment_id));
              if (String(e.reference_type || '').toLowerCase() === 'payment' && e.reference_id) {
                ids.push(String(e.reference_id));
              }
              return ids;
            })
        ),
      ].filter((id) => !paymentDetailsMap.has(id));
      if (journalPaymentIds.length) {
        const { data: extraPayRows } = await supabase
          .from('payments')
          .select('id, voided_at, reference_type, reference_id, reference_number, notes, payment_account_id')
          .eq('company_id', companyId)
          .in('id', journalPaymentIds);
        (extraPayRows || []).forEach((p: any) => {
          paymentDetailsMap.set(p.id, p);
          if (p.reference_type === 'sale' && p.reference_id && !saleStatusById.has(String(p.reference_id))) {
            saleStatusById.set(String(p.reference_id), '');
          }
        });
        const extraSaleIds = (extraPayRows || [])
          .filter((p: any) => String(p.reference_type || '').toLowerCase() === 'sale' && p.reference_id)
          .map((p: any) => String(p.reference_id));
        if (extraSaleIds.length) {
          const { data: extraSalesMeta } = await supabase
            .from('sales')
            .select('id, status')
            .eq('company_id', companyId)
            .in('id', [...new Set(extraSaleIds)]);
          (extraSalesMeta || []).forEach((s: any) => {
            if (s?.id) saleStatusById.set(String(s.id), String(s.status || ''));
          });
        }
      }

      // PHASE 4: Build ledger entries with running balance (from rangeLines only)
      console.log('[ACCOUNTING SERVICE] getCustomerLedger - PHASE 4: Building ledger entries', {
        rangeLines: rangeLines.length,
        openingBalance,
        sampleEntries: rangeLines.slice(0, 3).map((l: any) => ({
          entry_no: l.journal_entry?.entry_no,
          ref_type: l.journal_entry?.reference_type,
          ref_id: l.journal_entry?.reference_id,
          payment_id: l.journal_entry?.payment_id,
          debit: l.debit,
          credit: l.credit
        }))
      });

      const ledgerEntriesFromRange: AccountLedgerEntry[] = rangeLines.map((line: any) => {
        const entry = line.journal_entry;
        
        // STEP 3: BACKEND JOURNAL ENTRY RULE - Verify debit/credit are mutually exclusive
        const debit = line.debit || 0;
        const credit = line.credit || 0;
        
        // STEP 4: SQL VERIFICATION - Log if both debit and credit are non-zero (DATA CORRUPTION)
        if (debit > 0 && credit > 0) {
          console.error('[ACCOUNTING SERVICE] DATA CORRUPTION: Both debit and credit > 0:', {
            journal_entry_id: entry.id,
            entry_no: entry.entry_no,
            debit,
            credit,
            description: entry.description
          });
        }
        
        // STEP 6: Running Balance Formula for ASSET account: balance = previous + debit - credit
        runningBalance += debit - credit;

        // Determine source module and document type
        let sourceModule = 'Accounting';
        let documentType = 'Journal Entry';
        if (entry.reference_type === 'sale') {
          sourceModule = 'Sales';
          documentType = 'Sale Invoice';
        } else if (entry.reference_type === 'correction_reversal') {
          sourceModule = 'Accounting';
          documentType = 'Reversal';
        } else if (entry.reference_type === 'rental') {
          sourceModule = 'Rental';
          documentType = 'Rental';
        } else if (entry.payment_id) {
          sourceModule = 'Payment';
          documentType = 'Payment';
        } else if (entry.reference_type === 'expense') {
          sourceModule = 'Expense';
          documentType = 'Expense';
        }

        // Get Payment Account name (from journal_entry_lines.account or payment.payment_account)
        let accountName = '';
        // First try: Get from journal_entry_lines.account (for AR entries, this is the AR account itself)
        // For payment entries, we need the OTHER side (Cash/Bank account)
        if (entry.payment_id) {
          const payment = paymentDetailsMap.get(entry.payment_id);
          if (payment?.payment_account?.name) {
            accountName = payment.payment_account.name;
          } else if (payment?.payment_account_id && accountMap.has(payment.payment_account_id)) {
            accountName = accountMap.get(payment.payment_account_id) || '';
          } else if (payment?.payment_method) {
            // Fallback to payment_method if account not found
            accountName = payment.payment_method;
          }
        } else if (line.account) {
          // For non-payment entries, use the account from journal_entry_lines
          accountName = line.account.name || '';
        }

        // Get Notes (from payment.notes, separate from description)
        let notes = '';
        if (entry.payment_id) {
          const payment = paymentDetailsMap.get(entry.payment_id);
          if (payment?.notes) {
            notes = payment.notes;
          }
        }

        // CRITICAL FIX: Use entry_no from database as PRIMARY reference
        // Only generate fallback if entry_no is truly missing or invalid
        let referenceNumber = entry.entry_no;
        
        // Check if entry_no is missing, empty, or looks like UUID
        const isInvalidEntryNo = !referenceNumber || 
                                  referenceNumber.trim() === '' || 
                                  (referenceNumber.includes('-') && referenceNumber.length === 36) ||
                                  referenceNumber.length > 50;
        
        const sale = entry.reference_id ? salesMap.get(entry.reference_id) : null;
        const rental = entry.reference_type === 'rental' && entry.reference_id ? rentalsMap.get(entry.reference_id) : null;
        const paymentMeta = entry.payment_id ? paymentRefsMap.get(entry.payment_id) : undefined;
        const normalizedRefType = String(entry.reference_type || '').toLowerCase();
        const isReceiptCredit = credit > 0 && (
          Boolean(entry.payment_id) ||
          normalizedRefType === 'payment' ||
          (normalizedRefType === 'rental' && debit === 0)
        );
        const lineKind =
          normalizedRefType === 'sale' && debit > 0
            ? 'sale_debit'
            : normalizedRefType === 'rental' && debit > 0
              ? 'rental_debit'
              : isReceiptCredit
                ? 'receipt_credit'
                : 'journal';
        const resolvedRef = resolvePartyLedgerReference({
          lineKind,
          entryNo: entry.entry_no,
          referenceType: entry.reference_type,
          saleInvoiceNo: sale?.invoice_no,
          rentalBookingNo: rental?.booking_no,
          rentalPaymentRef: rentalPaymentRefByJeId.get(String(entry.id)) || null,
          paymentReferenceNumber: paymentMeta?.referenceNumber,
          paymentBankTraceId: paymentMeta?.bankTraceId,
        });
        if (resolvedRef && resolvedRef !== '—') {
          referenceNumber = resolvedRef;
        } else if (isInvalidEntryNo) {
          if (entry.payment_id) {
            referenceNumber = `PAY-${entry.payment_id.substring(0, 4).toUpperCase()}`;
          } else if (entry.reference_type === 'expense') {
            referenceNumber = `EXP-${entry.id.substring(0, 4).toUpperCase()}`;
          } else {
            // Last resort: Use entry ID substring (but this won't match database lookup)
            referenceNumber = `JE-${entry.id.substring(0, 4).toUpperCase()}`;
          }
        }
        
        // Get branch info from joined data; fall back to source document branch when JE.branch_id is null
        const branch = (entry as any).branch;
        let branchName = branch ? (branch.code ? `${branch.code} | ${branch.name}` : branch.name) : null;
        let resolvedBranchId = entry.branch_id as string | null | undefined;
        if (entry.reference_type === 'rental' && entry.reference_id) {
          const rb = rentalLedgerBranch(String(entry.reference_id));
          if (!resolvedBranchId) resolvedBranchId = rb.branch_id;
          if (!branchName) branchName = rb.branch_name;
        } else if (
          (entry.reference_type === 'sale' || entry.reference_type === 'sale_adjustment') &&
          entry.reference_id
        ) {
          const saleBranchId = salesMap.get(entry.reference_id)?.branch_id;
          if (!resolvedBranchId && saleBranchId) resolvedBranchId = String(saleBranchId);
          if (!branchName && saleBranchId) branchName = branchNameById.get(String(saleBranchId)) ?? null;
        }

        // STEP 2: DATA SOURCE CONFIRMATION - Ensure debit/credit are properly set
        const finalDebit = debit;
        const finalCredit = credit;
        
        // Verify: Dono aik sath non-zero NA hon
        if (finalDebit > 0 && finalCredit > 0) {
          console.warn('[ACCOUNTING SERVICE] Invalid entry: Both debit and credit non-zero:', {
            entry_id: entry.id,
            entry_no: entry.entry_no,
            debit: finalDebit,
            credit: finalCredit
          });
        }

        const linkedSaleId =
          entry.reference_id &&
          ['sale', 'sale_reversal', 'sale_return', 'gl_correction'].includes(normalizedRefType)
            ? String(entry.reference_id)
            : undefined;

        const payId =
          entry.payment_id ||
          (normalizedRefType === 'payment' && entry.reference_id ? String(entry.reference_id) : null);
        const payDetail = payId ? paymentDetailsMap.get(payId) : undefined;
        const paymentSaleId =
          payDetail &&
          String(payDetail.reference_type || '').toLowerCase() === 'sale' &&
          payDetail.reference_id
            ? String(payDetail.reference_id)
            : null;
        const resolvedLinkedSaleId = linkedSaleId ?? paymentSaleId ?? undefined;

        return {
          date: entry.entry_date,
          reference_number: referenceNumber,
          // CRITICAL: Store actual entry_no from database (for lookup)
          // If entry_no exists in DB, use it. Otherwise, use referenceNumber (which is generated from entry_no or UUID)
          entry_no: entry.entry_no || null, // Keep actual DB entry_no, don't fallback to generated referenceNumber
          description: mergeLedgerEntryDescriptionForStatement(entry.reference_type, entry.description, line.description) || 'Journal Entry',
          // STEP 1: DIRECT mapping - NO Math.abs(), NO conditionals
          debit: finalDebit,
          credit: finalCredit,
          running_balance: runningBalance,
          source_module: sourceModule,
          document_type: documentType,
          account_name: accountName,
          notes: notes,
          created_by: entry.created_by,
          journal_entry_id: entry.id, // Use journal_entry_id as fallback if entry_no is missing
          je_reference_type: entry.reference_type || undefined,
          je_action_fingerprint: (entry as { action_fingerprint?: string | null }).action_fingerprint ?? null,
          payment_id:
            entry.payment_id ||
            (entry.reference_type === 'payment' && entry.reference_id ? String(entry.reference_id) : undefined),
          sale_id: resolvedLinkedSaleId,
          linked_sale_status: resolvedLinkedSaleId ? saleStatusById.get(resolvedLinkedSaleId) ?? null : null,
          payment_voided_at: payDetail?.voided_at ?? null,
          rental_id: entry.reference_type === 'rental' ? entry.reference_id : undefined,
          branch_id: resolvedBranchId ?? entry.branch_id,
          branch_name: branchName,
          ledger_kind: entry.reference_type === 'correction_reversal' ? ('reversal' as const) : undefined,
        };
      });

      // Fallback: no journal entries but we have sales/payments/rentals – build ledger from RPC data (operational only — never in gl_journal_only)
      if (
        !glJournalOnly &&
        ledgerEntriesFromRange.length === 0 &&
        (customerSales.length > 0 || customerPayments.length > 0 || customerRentals.length > 0)
      ) {
        const items: { date: string; reference_number: string; description: string; debit: number; credit: number; sale_id?: string; payment_id?: string; rental_id?: string; source_module: string; document_type: string }[] = [];
        customerSales.forEach((s: any) => {
          const d = (s.invoice_date || '').toString();
          if (startDate && d < startDate) return;
          if (endDate && d > endDate) return;
          const saleTotal = (Number(s.total) || 0) + (Number(s.shipment_charges) ?? 0);
          items.push({
            date: d,
            reference_number: (s.invoice_no || `SALE-${s.id?.slice(0, 8)}`).toString(),
            description: 'Sale',
            debit: saleTotal,
            credit: 0,
            sale_id: s.id,
            source_module: 'Sales',
            document_type: (s.invoice_no || '').toString().toUpperCase().startsWith('STD-') || (s.invoice_no || '').toString().toUpperCase().startsWith('ST-') ? 'Studio Sale' : 'Sale Invoice',
          });
        });
        customerPayments.forEach((p: any) => {
          const d = (p.payment_date || '').toString();
          if (startDate && d < startDate) return;
          if (endDate && d > endDate) return;
          const saleStatus =
            String(p.reference_type || '') === 'sale' && p.reference_id
              ? saleStatusById.get(String(p.reference_id))
              : undefined;
          items.push({
            date: d,
            reference_number: resolvePartyLedgerReference({
              lineKind: 'receipt_credit',
              paymentReferenceNumber: p.reference_number,
            }),
            description: customerReceiptLedgerDescription({ saleStatus, base: 'Payment' }),
            debit: 0,
            credit: Number(p.amount) || 0,
            payment_id: p.id,
            source_module: 'Payment',
            document_type: 'Payment',
          });
        });
        // Rental charges (debit – customer owes)
        customerRentals.forEach((r: any) => {
          const rawDate = r.pickup_date || r.booking_date || r.created_at;
          const d = rawDate ? (typeof rawDate === 'string' && rawDate.length >= 10 ? rawDate.slice(0, 10) : new Date(rawDate).toISOString().slice(0, 10)) : '';
          if (!d) return;
          if (startDate && d < startDate) return;
          if (endDate && d > endDate) return;
          const total = Number(r.total_amount) || 0;
          if (total <= 0) return;
          items.push({
            date: d,
            reference_number: (r.booking_no || `RN-${r.id?.slice(0, 8)}`).toString(),
            description: 'Rental Charge',
            debit: total,
            credit: 0,
            rental_id: r.id,
            source_module: 'Rental',
            document_type: 'Rental Invoice',
          });
        });
        // Rental payments (credit – customer paid; penalty = debit charge + credit receipt)
        customerRentalPayments.forEach((p: any) => {
          appendRentalPaymentMergeItems(items, {
            payment: p,
            rental: rentalsMap.get(p.rental_id),
            customerDisplayName,
            ledgerRows: items,
            startDate,
            endDate,
          });
        });
        items.sort((a, b) => a.date.localeCompare(b.date) || 0);
        let runBal = 0;
        const syntheticEntries: AccountLedgerEntry[] = items.map((item) => {
          runBal += item.debit - item.credit;
          const branchFields = item.rental_id ? rentalLedgerBranch(item.rental_id) : {};
          return {
            date: item.date,
            reference_number: item.reference_number,
            entry_no: null,
            description: item.description,
            debit: item.debit,
            credit: item.credit,
            running_balance: runBal,
            source_module: item.source_module,
            document_type: item.document_type,
            journal_entry_id: '',
            account_name: '',
            notes: '',
            sale_id: item.sale_id,
            payment_id: item.payment_id,
            rental_id: item.rental_id,
            ...branchFields,
          };
        });
        const withOpening: AccountLedgerEntry[] = startDate
          ? [
              { date: startDate, reference_number: '-', entry_no: null, description: 'Opening Balance', debit: 0, credit: 0, running_balance: 0, source_module: 'Accounting', journal_entry_id: '', document_type: 'Opening Balance', account_name: '', notes: '' },
              ...syntheticEntries,
            ]
          : syntheticEntries;
        console.log('[ACCOUNTING SERVICE] getCustomerLedger - Using synthetic ledger from sales/payments (no journal entries):', syntheticEntries.length);
        return withOpening;
      }

      // MERGE: Add any sales/payments/rentals that have no journal line (rentals never hit AR, so always add synthetic)
      const saleIdsInJournal = new Set((ledgerEntriesFromRange.map((e: AccountLedgerEntry) => e.sale_id).filter(Boolean)) as string[]);
      const paymentIdsInJournal = new Set((ledgerEntriesFromRange.map((e: AccountLedgerEntry) => e.payment_id).filter(Boolean)) as string[]);
      const rentalIdsInJournal = new Set((ledgerEntriesFromRange.map((e: AccountLedgerEntry) => e.rental_id).filter(Boolean)) as string[]);
      const missingSales = customerSales.filter((s: any) => !saleIdsInJournal.has(s.id));
      let missingPayments = customerPayments.filter((p: any) => !paymentIdsInJournal.has(p.id));
      if (missingPayments.length > 0) {
        missingPayments = await filterLivePaymentsExcludingVoidedJournals(companyId, missingPayments);
      }
      const hasRentalsToAdd = customerRentals.length > 0 || customerRentalPayments.length > 0;
      if (!glJournalOnly && (missingSales.length > 0 || missingPayments.length > 0 || hasRentalsToAdd)) {
        const mergeItems: { date: string; reference_number: string; description: string; debit: number; credit: number; sale_id?: string; payment_id?: string; rental_id?: string; source_module: string; document_type: string }[] = [];
        missingSales.forEach((s: any) => {
          const d = (s.invoice_date || '').toString();
          if (startDate && d < startDate) return;
          if (endDate && d > endDate) return;
          const saleTotal = (Number(s.total) || 0) + (Number(s.shipment_charges) ?? 0);
          mergeItems.push({
            date: d,
            reference_number: (s.invoice_no || `SALE-${s.id?.slice(0, 8)}`).toString(),
            description: 'Sale',
            debit: saleTotal,
            credit: 0,
            sale_id: s.id,
            source_module: 'Sales',
            document_type: (s.invoice_no || '').toString().toUpperCase().startsWith('STD-') || (s.invoice_no || '').toString().toUpperCase().startsWith('ST-') ? 'Studio Sale' : 'Sale Invoice',
          });
        });
        missingPayments.forEach((p: any) => {
          const d = (p.payment_date || '').toString();
          if (startDate && d < startDate) return;
          if (endDate && d > endDate) return;
          const saleStatus =
            String(p.reference_type || '') === 'sale' && p.reference_id
              ? saleStatusById.get(String(p.reference_id))
              : undefined;
          const refType = String(p.reference_type || '').toLowerCase();
          mergeItems.push({
            date: d,
            reference_number: resolvePartyLedgerReference({
              lineKind: 'receipt_credit',
              paymentReferenceNumber: p.reference_number,
            }),
            description:
              refType === 'on_account' && p.notes
                ? String(p.notes)
                : customerReceiptLedgerDescription({ saleStatus, base: 'Payment' }),
            debit: 0,
            credit: Number(p.amount) || 0,
            payment_id: p.id,
            source_module: 'Payment',
            document_type: 'Payment',
          });
        });
        // Rentals: synthetic charge only when no AR journal row already carries this rental_id
        customerRentals.forEach((r: any) => {
          const rid = r?.id != null ? String(r.id) : '';
          if (rid && rentalIdsInJournal.has(rid)) return;
          const rawDate = r.pickup_date || r.booking_date || r.created_at;
          const d = rawDate ? (typeof rawDate === 'string' && rawDate.length >= 10 ? rawDate.slice(0, 10) : new Date(rawDate).toISOString().slice(0, 10)) : '';
          if (!d) return;
          if (startDate && d < startDate) return;
          if (endDate && d > endDate) return;
          const total = Number(r.total_amount) || 0;
          if (total <= 0) return;
          mergeItems.push({
            date: d,
            reference_number: (r.booking_no || `RN-${r.id?.slice(0, 8)}`).toString(),
            description: 'Rental Charge',
            debit: total,
            credit: 0,
            rental_id: r.id,
            source_module: 'Rental',
            document_type: 'Rental Invoice',
          });
        });
        customerRentalPayments.forEach((p: any) => {
          appendRentalPaymentMergeItems(mergeItems, {
            payment: p,
            rental: rentalsMap.get(p.rental_id),
            customerDisplayName,
            ledgerRows: [...ledgerEntriesFromRange, ...mergeItems],
            startDate,
            endDate,
          });
        });
        if (mergeItems.length > 0) {
          const syntheticMissing: AccountLedgerEntry[] = mergeItems.map((item) => ({
            date: item.date,
            reference_number: item.reference_number,
            entry_no: null,
            description: item.description,
            debit: item.debit,
            credit: item.credit,
            running_balance: 0,
            source_module: item.source_module,
            document_type: item.document_type,
            journal_entry_id: '',
            account_name: '',
            notes: '',
            sale_id: item.sale_id,
            payment_id: item.payment_id,
            rental_id: item.rental_id,
            ...(item.rental_id ? rentalLedgerBranch(item.rental_id) : {}),
          }));
          const combined = [...ledgerEntriesFromRange, ...syntheticMissing].sort((a, b) => a.date.localeCompare(b.date) || 0);
          let runBal = openingBalance;
          combined.forEach((e) => {
            runBal += (e.debit || 0) - (e.credit || 0);
            e.running_balance = runBal;
          });
          console.log('[ACCOUNTING SERVICE] getCustomerLedger - Merged', syntheticMissing.length, 'missing sales/payments into ledger');
          const ledgerEntries: AccountLedgerEntry[] = startDate
            ? [{ date: startDate, reference_number: '-', entry_no: null, description: 'Opening Balance', debit: 0, credit: 0, running_balance: openingBalance, source_module: 'Accounting', journal_entry_id: '', document_type: 'Opening Balance', account_name: '', notes: '' }, ...combined]
            : combined;
          return ledgerEntries;
        }
      }

      // When date range is applied, prepend opening balance row so UI/PDF/Print show it and totals are correct
      const ledgerEntries: AccountLedgerEntry[] = startDate
        ? [
            {
              date: startDate,
              reference_number: '-',
              entry_no: null,
              description: 'Opening Balance',
              debit: 0,
              credit: 0,
              running_balance: openingBalance,
              source_module: 'Accounting',
              journal_entry_id: '',
              document_type: 'Opening Balance',
              account_name: '',
              notes: '',
            },
            ...ledgerEntriesFromRange,
          ]
        : ledgerEntriesFromRange;

      return ledgerEntries;
    } catch (error: any) {
      console.error('[ACCOUNTING SERVICE] Error getting customer ledger:', error);
      return [];
    }
  },

  /**
   * Supplier AP: **control 2000 only**, same party resolution as `get_contact_party_gl_balances` (RPC `get_supplier_ap_gl_ledger_for_contact`).
   * Convention: credit − debit on AP liability. Falls back to legacy client filter if RPC is missing.
   * Path 21: operational supplier view excludes Agent FX credit JEs and fx_currency_purchase_settlements payments
   * (same AP child can hold both roles when a supplier was wrongly used as agent).
   */
  async getSupplierApGlJournalLedger(
    supplierId: string,
    companyId: string,
    branchId?: string,
    startDate?: string,
    endDate?: string,
    opts?: { partyRole?: 'supplier' | 'agent_fx' | 'all' }
  ): Promise<AccountLedgerEntry[]> {
    const partyRole = opts?.partyRole ?? 'supplier';
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const safeBranchId =
      branchId && branchId !== 'all' && typeof branchId === 'string' && uuidRegex.test(branchId.trim())
        ? branchId.trim()
        : null;
    const startStr = startDate ? startDate.slice(0, 10) : null;
    const endStr = endDate ? endDate.slice(0, 10) : null;

    const loadAgentSettlementPaymentIds = async (): Promise<Set<string>> => {
      if (partyRole === 'all') return new Set();
      const { data } = await supabase
        .from('fx_currency_purchase_settlements')
        .select('payment_id, status, voided_at')
        .eq('company_id', companyId);
      return new Set(
        (data || [])
          .filter((r: { status?: string | null; voided_at?: string | null }) => {
            if (r.voided_at) return false;
            const st = String(r.status || 'active').toLowerCase();
            return st === 'active' || st === '';
          })
          .map((r: { payment_id?: string }) => (r.payment_id ? String(r.payment_id) : ''))
          .filter(Boolean)
      );
    };

    const mapRpcRows = (rawRows: Record<string, unknown>[]): AccountLedgerEntry[] =>
      rawRows.map((r) => {
        const debit = Number(r.debit ?? 0);
        const credit = Number(r.credit ?? 0);
        const refType = r.reference_type != null ? String(r.reference_type) : '';
        return {
          date: String(r.entry_date ?? '').slice(0, 10),
          created_at: r.created_at != null ? String(r.created_at) : undefined,
          reference_number: (r.entry_no != null && String(r.entry_no).trim() !== ''
            ? String(r.entry_no)
            : String(r.journal_entry_id || '').slice(0, 8)) || '—',
          entry_no: r.entry_no != null ? String(r.entry_no) : undefined,
          description: String(r.description ?? '—'),
          debit,
          credit,
          running_balance: Number(r.running_balance ?? 0),
          source_module: glStatementSourceModuleFromReferenceType(refType),
          journal_entry_id: String(r.journal_entry_id ?? ''),
          payment_id: r.payment_id != null ? String(r.payment_id) : undefined,
          je_reference_type: refType || undefined,
          branch_id: r.branch_id != null ? String(r.branch_id) : undefined,
          branch_name: r.branch_name != null ? String(r.branch_name) : undefined,
          account_name: String(r.account_name ?? ''),
          gl_account_code: String(r.account_code ?? ''),
          document_type: glStatementDocumentTypeFromReferenceType(refType, 'AP Journal'),
        };
      });

    try {
      // Wave 0: for role views, load full history through endDate so opening matches filtered rows
      const rpcStart =
        partyRole !== 'all' && startStr ? null : startStr;

      const [{ data: rpcRaw, error: rpcError }, agentSettlementPaymentIds] = await Promise.all([
        supabase.rpc('get_supplier_ap_gl_ledger_for_contact', {
          p_company_id: companyId,
          p_supplier_id: supplierId,
          p_branch_id: safeBranchId,
          p_start_date: rpcStart,
          p_end_date: endStr,
        }),
        loadAgentSettlementPaymentIds(),
      ]);

      if (rpcError) {
        console.warn('[ACCOUNTING SERVICE] get_supplier_ap_gl_ledger_for_contact:', rpcError.message);
        const legacy = await fetchSupplierApGlJournalLedgerLegacy(supplierId, companyId, branchId, startDate, endDate);
        if (partyRole === 'all') return legacy;
        const filtered = filterApRowsByPartyRole(legacy, partyRole, agentSettlementPaymentIds);
        const { opening, inRange } = splitRoleFilteredApRowsByPeriod(filtered, startStr);
        const openingRow: AccountLedgerEntry | null = startStr
          ? {
              date: startStr,
              reference_number: '—',
              description: 'Opening Balance (AP GL)',
              debit: 0,
              credit: 0,
              running_balance: opening,
              source_module: 'Accounting',
              journal_entry_id: '',
              document_type: 'Opening Balance',
              account_name: '',
            }
          : null;
        const body = openingRow ? [openingRow, ...inRange] : inRange;
        return recalcApLiabilityRunningBalances(body, opening);
      }

      const payload = rpcRaw as { period_opening_balance?: number; rows?: Record<string, unknown>[] } | null;
      if (!payload || typeof payload !== 'object') {
        const legacy = await fetchSupplierApGlJournalLedgerLegacy(supplierId, companyId, branchId, startDate, endDate);
        if (partyRole === 'all') return legacy;
        const filtered = filterApRowsByPartyRole(legacy, partyRole, agentSettlementPaymentIds);
        const { opening, inRange } = splitRoleFilteredApRowsByPeriod(filtered, startStr);
        return recalcApLiabilityRunningBalances(inRange, opening);
      }

      let ledgerEntriesFromRange: AccountLedgerEntry[] = mapRpcRows(
        Array.isArray(payload.rows) ? payload.rows : []
      );

      if (partyRole === 'all') {
        const opening = Number(payload.period_opening_balance ?? 0);
        if (startStr) {
          const openingRow: AccountLedgerEntry = {
            date: startStr,
            reference_number: '—',
            entry_no: undefined,
            description: 'Opening Balance (AP GL)',
            debit: 0,
            credit: 0,
            running_balance: opening,
            source_module: 'Accounting',
            journal_entry_id: '',
            document_type: 'Opening Balance',
            account_name: '',
          };
          return [openingRow, ...ledgerEntriesFromRange];
        }
        return ledgerEntriesFromRange;
      }

      ledgerEntriesFromRange = filterApRowsByPartyRole(
        ledgerEntriesFromRange,
        partyRole,
        agentSettlementPaymentIds
      );
      const { opening, inRange } = splitRoleFilteredApRowsByPeriod(ledgerEntriesFromRange, startStr);
      const openingRow: AccountLedgerEntry | null = startStr
        ? {
            date: startStr,
            reference_number: '—',
            entry_no: undefined,
            description: 'Opening Balance (AP GL)',
            debit: 0,
            credit: 0,
            running_balance: opening,
            source_module: 'Accounting',
            journal_entry_id: '',
            document_type: 'Opening Balance',
            account_name: '',
          }
        : null;
      const body = openingRow ? [openingRow, ...inRange] : inRange;
      return recalcApLiabilityRunningBalances(body, opening);
    } catch (e) {
      console.error('[ACCOUNTING SERVICE] getSupplierApGlJournalLedger:', e);
      return fetchSupplierApGlJournalLedgerLegacy(supplierId, companyId, branchId, startDate, endDate);
    }
  },

  /**
   * Customer AR: **control 1100 only**, same party resolution as `get_contact_party_gl_balances` (RPC `get_customer_ar_gl_ledger_for_contact`).
   * Convention: running balance asset-style (debit − credit on 1100).
   */
  async getCustomerArGlJournalLedger(
    customerId: string,
    companyId: string,
    branchId?: string,
    startDate?: string,
    endDate?: string
  ): Promise<AccountLedgerEntry[]> {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const safeBranchId =
      branchId && branchId !== 'all' && typeof branchId === 'string' && uuidRegex.test(branchId.trim())
        ? branchId.trim()
        : null;
    const startStr = startDate ? startDate.slice(0, 10) : null;
    const endStr = endDate ? endDate.slice(0, 10) : null;

    try {
      const { data: rpcRaw, error: rpcError } = await supabase.rpc('get_customer_ar_gl_ledger_for_contact', {
        p_company_id: companyId,
        p_customer_id: customerId,
        p_branch_id: safeBranchId,
        p_start_date: startStr,
        p_end_date: endStr,
      });

      if (rpcError) {
        console.warn('[ACCOUNTING SERVICE] get_customer_ar_gl_ledger_for_contact:', rpcError.message);
        return [];
      }

      const payload = rpcRaw as { period_opening_balance?: number; rows?: Record<string, unknown>[] } | null;
      if (!payload || typeof payload !== 'object') {
        return [];
      }

      const opening = Number(payload.period_opening_balance ?? 0);
      const rawRows = Array.isArray(payload.rows) ? payload.rows : [];

      const ledgerEntriesFromRange: AccountLedgerEntry[] = rawRows.map((r) => {
        const debit = Number(r.debit ?? 0);
        const credit = Number(r.credit ?? 0);
        const refType = r.reference_type != null ? String(r.reference_type) : '';
        return {
          date: String(r.entry_date ?? '').slice(0, 10),
          created_at: r.created_at != null ? String(r.created_at) : undefined,
          reference_number:
            r.entry_no != null && String(r.entry_no).trim() !== ''
              ? String(r.entry_no)
              : (String(r.journal_entry_id || '').slice(0, 8) || '—'),
          entry_no: r.entry_no != null ? String(r.entry_no) : undefined,
          description: String(r.description ?? '—'),
          debit,
          credit,
          running_balance: Number(r.running_balance ?? 0),
          source_module: glStatementSourceModuleFromReferenceType(refType),
          journal_entry_id: String(r.journal_entry_id ?? ''),
          journal_line_id:
            r.journal_entry_line_id != null ? String(r.journal_entry_line_id) : undefined,
          je_reference_type: refType || undefined,
          payment_id: r.payment_id != null ? String(r.payment_id) : undefined,
          branch_id: r.branch_id != null ? String(r.branch_id) : undefined,
          branch_name: r.branch_name != null ? String(r.branch_name) : undefined,
          account_name: String(r.account_name ?? ''),
          gl_account_code: String(r.account_code ?? ''),
          document_type: glStatementDocumentTypeFromReferenceType(refType, 'AR Journal'),
        };
      });

      if (startStr) {
        return [
          {
            date: startStr,
            reference_number: '—',
            entry_no: undefined,
            description: 'Opening Balance (AR GL)',
            debit: 0,
            credit: 0,
            running_balance: opening,
            source_module: 'Accounting',
            journal_entry_id: '',
            document_type: 'Opening Balance',
            account_name: '',
          },
          ...ledgerEntriesFromRange,
        ];
      }

      return ledgerEntriesFromRange;
    } catch (e) {
      console.error('[ACCOUNTING SERVICE] getCustomerArGlJournalLedger:', e);
      return [];
    }
  },

  /**
   * Worker party: journal lines on Worker Payable (2010) and Worker Advance (1180) only — no worker_ledger_entries merge.
   * running_balance = net GL exposure (WP liability credit−debit minus WA asset debit−credit), same basis as get_contact_party_gl_balances worker slice.
   */
  async getWorkerPartyGlJournalLedger(
    workerId: string,
    companyId: string,
    branchId?: string,
    startDate?: string,
    endDate?: string
  ): Promise<AccountLedgerEntry[]> {
    try {
      const { data: accts } = await supabase
        .from('accounts')
        .select('id, code, name')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .or('code.eq.2010,code.eq.1180,name.ilike.%Worker Payable%,name.ilike.%Worker Advance%');
      const wpWa = (accts || []).filter((a: any) => {
        const c = String(a.code || '').trim();
        return c === '2010' || c === '1180';
      });
      if (wpWa.length === 0) return [];
      const accountIds = wpWa.map((a: any) => a.id);
      const codeById = new Map(wpWa.map((a: any) => [a.id, String(a.code || '').trim()]));
      const nameById = new Map(wpWa.map((a: any) => [a.id, String(a.name || '')]));

      const { data: lines, error } = await supabase
        .from('journal_entry_lines')
        .select(
          `
          *,
          account:accounts(id, name, code),
          journal_entry:journal_entries(
            id, entry_no, entry_date, description, reference_type, reference_id, payment_id,
            branch_id, created_by, created_at, is_void,
            branch:branches(id, name, code)
          )
        `
        )
        .in('account_id', accountIds)
        .order('created_at', { ascending: true });

      if (error) console.error('[ACCOUNTING SERVICE] getWorkerPartyGlJournalLedger lines error:', error);
      const linesToUse = (lines || []).filter((l: any) => (l.journal_entry && (l.journal_entry as any).is_void) !== true);

      const { data: workerPayments } = await supabase
        .from('payments')
        .select('id')
        .eq('company_id', companyId)
        .eq('contact_id', workerId);
      const workerPaymentIds = new Set((workerPayments || []).map((p: { id: string }) => p.id));

      const { data: workerStages } = await supabase
        .from('studio_production_stages')
        .select('id')
        .eq('assigned_worker_id', workerId);
      const workerStageIds = new Set((workerStages || []).map((s: { id: string }) => String(s.id)));

      const workerLines = linesToUse.filter((line: any) => {
        const entry = line.journal_entry;
        if (!entry) return false;
        const rt = String(entry.reference_type || '').toLowerCase();
        if (rt === 'correction_reversal' && entry.reference_id) {
          const origId = String(entry.reference_id);
          return linesToUse.some((ol: any) => {
            const oe = ol.journal_entry;
            if (!oe || String(oe.id) !== origId) return false;
            return workerGlLineMatchesWorker(ol, workerId, workerPaymentIds, workerStageIds);
          });
        }
        return workerGlLineMatchesWorker(line, workerId, workerPaymentIds, workerStageIds);
      });

      const branchFiltered = branchId
        ? workerLines.filter((line: any) => {
            const bid = line.journal_entry?.branch_id;
            return bid == null || bid === '' || bid === branchId;
          })
        : workerLines;

      let openWp = 0;
      let openWa = 0;
      if (startDate) {
        branchFiltered.forEach((line: any) => {
          const entry = line.journal_entry;
          if (!entry || entry.entry_date >= startDate) return;
          const aid = line.account_id;
          const code = codeById.get(aid) || '';
          const debit = line.debit || 0;
          const credit = line.credit || 0;
          if (code === '2010') openWp += credit - debit;
          else if (code === '1180') openWa += debit - credit;
        });
      }
      let openingNet = openWp - openWa;

      const rangeLines = branchFiltered.filter((line: any) => {
        const entry = line.journal_entry;
        if (!entry) return false;
        const entryDate = entry.entry_date;
        if (startDate && entryDate < startDate) return false;
        if (endDate && entryDate > endDate) return false;
        return true;
      });

      rangeLines.sort((a: any, b: any) => {
        const dateA = String(a.journal_entry?.entry_date || '');
        const dateB = String(b.journal_entry?.entry_date || '');
        if (dateA !== dateB) return dateA.localeCompare(dateB);
        const createdA = String(a.journal_entry?.created_at || a.created_at || '');
        const createdB = String(b.journal_entry?.created_at || b.created_at || '');
        return createdA.localeCompare(createdB);
      });

      let wpRun = openWp;
      let waRun = openWa;
      let runningBalance = openingNet;

      const ledgerEntriesFromRange: AccountLedgerEntry[] = rangeLines.map((line: any) => {
        const entry = line.journal_entry;
        const debit = line.debit || 0;
        const credit = line.credit || 0;
        const aid = line.account_id;
        const code = codeById.get(aid) || String(line.account?.code || '').trim();
        if (code === '2010') wpRun += credit - debit;
        else if (code === '1180') waRun += debit - credit;
        runningBalance = wpRun - waRun;
        return {
          date: entry.entry_date,
          created_at: entry.created_at,
          reference_number: entry.entry_no || entry.id?.slice(0, 8) || '—',
          entry_no: entry.entry_no,
          description: `${nameById.get(aid) || line.account?.name || code} — ${mergeLedgerEntryDescriptionForStatement(entry.reference_type, entry.description, line.description)}`,
          debit,
          credit,
          running_balance: runningBalance,
          source_module: 'Accounting',
          journal_entry_id: entry.id,
          payment_id: entry.payment_id,
          branch_id: entry.branch_id,
          branch_name: entry.branch?.name,
          account_name: line.account?.name || '',
          gl_account_code: code,
          document_type: code === '2010' ? 'Worker Payable' : 'Worker Advance',
        };
      });

      const ledgerEntries: AccountLedgerEntry[] = startDate
        ? [
            {
              date: startDate,
              reference_number: '—',
              entry_no: undefined,
              description: 'Opening Balance (WP/WA GL net)',
              debit: 0,
              credit: 0,
              running_balance: openingNet,
              source_module: 'Accounting',
              journal_entry_id: '',
              document_type: 'Opening Balance',
              account_name: '',
            },
            ...ledgerEntriesFromRange,
          ]
        : ledgerEntriesFromRange;

      return ledgerEntries;
    } catch (e) {
      console.error('[ACCOUNTING SERVICE] getWorkerPartyGlJournalLedger:', e);
      return [];
    }
  },

  /**
   * Create journal entry with lines.
   * Canonical rule: when the entry touches Cash/Bank/Wallet (payment account), callers must create
   * a payments row first and pass paymentId so Roznamcha shows the transaction.
   */
  async createEntry(entry: JournalEntry, lines: JournalEntryLine[], paymentId?: string) {
    // Validate double-entry: total_debit must equal total_credit
    const totalDebit = lines.reduce((sum, line) => sum + (line.debit || 0), 0);
    const totalCredit = lines.reduce((sum, line) => sum + (line.credit || 0), 0);

    if (Math.abs(totalDebit - totalCredit) > 0.01) {
      const isExpense = entry.reference_type === 'expense' || entry.reference_type === 'extra_expense';
      if (typeof window !== 'undefined' && isExpense) {
        console.error('[ACCOUNTING] Unbalanced expense entry blocked:', {
          reference_type: entry.reference_type,
          totalDebit,
          totalCredit,
          lineCount: lines.length,
        });
      }
      throw new Error(`Double-entry validation failed: Debit (${totalDebit}) must equal Credit (${totalCredit})`);
    }

    // Create journal entry header (total_debit/total_credit filled after lines insert)
    // CRITICAL FIX: Only include fields that are not null/undefined to prevent "undefinedundefined" UUID error
    const insertData: any = {
      company_id: entry.company_id,
      entry_no: entry.entry_no,
      entry_date: toLocalDateString(entry.entry_date),
      description: entry.description,
      reference_type: entry.reference_type,
    };
    const mirroredDoc =
      (entry.document_no && String(entry.document_no).trim()) ||
      (entry.entry_no && /^(JV|FT)-/i.test(entry.entry_no) ? entry.entry_no : null);
    if (mirroredDoc) {
      insertData.document_no = mirroredDoc;
    }
    
    // Only add optional UUID fields if they have valid values (not null/undefined)
    // CRITICAL FIX: Validate branch_id - must be valid UUID, not "all"
    if (entry.branch_id && entry.branch_id !== 'all') {
      insertData.branch_id = entry.branch_id;
    }
    if (entry.reference_id) {
      insertData.reference_id = entry.reference_id;
    }
    if (entry.created_by) {
      insertData.created_by = entry.created_by;
    }
    // CRITICAL: Link journal entry to payment if provided
    if (paymentId) {
      insertData.payment_id = paymentId;
    }
    // Payment contract (Phase 5): mirror payments.id onto reference_id when unset (except document-root JEs and
    // manual_receipt, which may use contact_id on reference_id for customer ledger matching).
    if (paymentId && !insertData.reference_id) {
      const rt = String(entry.reference_type || '').toLowerCase();
      if (rt !== 'sale' && rt !== 'purchase' && rt !== 'manual_receipt') {
        insertData.reference_id = paymentId;
      }
    }
    // Standalone payment / payment_adjustment rows: always keep reference_id = payment_id when linked
    if (paymentId) {
      const rt = String(entry.reference_type || '').toLowerCase();
      if (rt === 'payment' || rt === 'payment_adjustment') {
        insertData.reference_id = paymentId;
      }
    }
    if (entry.attachments !== undefined && entry.attachments != null && Array.isArray(entry.attachments) && entry.attachments.length > 0) {
      insertData.attachments = JSON.parse(JSON.stringify(entry.attachments));
    }
    if (entry.action_fingerprint) {
      insertData.action_fingerprint = entry.action_fingerprint;
    }
    if (entry.economic_event_id) {
      insertData.economic_event_id = entry.economic_event_id;
    }

    // STEP 2 FIX: Fix journal_entries query - use proper select instead of select()
    let result = await supabase.from('journal_entries').insert(insertData).select('*').single();
    let entryData = result.data;
    let entryError = result.error;
    if (entryError && entryError.code === 'PGRST204' && entryError.message?.includes('attachments')) {
      delete insertData.attachments;
      result = await supabase.from('journal_entries').insert(insertData).select('*').single();
      entryData = result.data;
      entryError = result.error;
    }
    if (entryError && entryError.code === 'PGRST204' && entryError.message?.includes('document_no')) {
      delete insertData.document_no;
      result = await supabase.from('journal_entries').insert(insertData).select('*').single();
      entryData = result.data;
      entryError = result.error;
    }

    // CRITICAL FIX: Handle missing table error with helpful message
    if (entryError && (entryError.code === 'PGRST205' || entryError.message?.includes('does not exist'))) {
      const errorMessage = `❌ Journal Entries table not found!\n\nThe 'journal_entries' table doesn't exist in your database. Please run this SQL in your Supabase SQL Editor:\n\nCREATE TABLE IF NOT EXISTS journal_entries (\n  id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),\n  company_id UUID NOT NULL REFERENCES companies(id) ON DELETE CASCADE,\n  branch_id UUID REFERENCES branches(id) ON DELETE SET NULL,\n  entry_no VARCHAR(100),\n  entry_date DATE NOT NULL,\n  description TEXT,\n  reference_type VARCHAR(50),\n  reference_id UUID,\n  created_by UUID REFERENCES users(id) ON DELETE SET NULL,\n  created_at TIMESTAMPTZ DEFAULT NOW(),\n  updated_at TIMESTAMPTZ DEFAULT NOW()\n);\n\nCREATE TABLE IF NOT EXISTS journal_entry_lines (\n  id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),\n  journal_entry_id UUID NOT NULL REFERENCES journal_entries(id) ON DELETE CASCADE,\n  account_id UUID NOT NULL REFERENCES accounts(id) ON DELETE RESTRICT,\n  debit DECIMAL(15,2) DEFAULT 0,\n  credit DECIMAL(15,2) DEFAULT 0,\n  description TEXT,\n  created_at TIMESTAMPTZ DEFAULT NOW()\n);\n\nSee: supabase-extract/migrations/03_frontend_driven_schema.sql for complete schema.`;
      throw new Error(errorMessage);
    }

    // Idempotent recovery: duplicate key / 409 (e.g. idx_journal_entries_fingerprint_active or parallel sale JE)
    const dup =
      entryError &&
      (entryError.code === '23505' ||
        (entryError as { status?: number }).status === 409 ||
        String(entryError.message || '').toLowerCase().includes('duplicate') ||
        String(entryError.message || '').toLowerCase().includes('unique'));
    // Canonical document sale JE only (payment receipts also use reference_type=sale + payment_id).
    if (
      dup &&
      entry.reference_type === 'sale' &&
      entry.reference_id &&
      !insertData.payment_id
    ) {
      const { data: existingRows } = await supabase
        .from('journal_entries')
        .select('*')
        .eq('reference_type', 'sale')
        .eq('reference_id', entry.reference_id)
        .is('payment_id', null)
        .or('is_void.is.null,is_void.eq.false')
        .order('created_at', { ascending: true })
        .limit(1);
      const existing = (existingRows as Record<string, unknown>[] | null)?.[0] as
        | { id: string; is_void?: boolean }
        | undefined;
      if (existing?.id && existing.is_void !== true) {
        if (import.meta.env?.DEV) {
          console.warn('[accountingService] Duplicate canonical sale JE insert — returning existing row:', existing.id);
        }
        return { ...existing, lines: [] };
      }
    }
    if (
      dup &&
      entry.reference_type === 'purchase' &&
      entry.reference_id &&
      !insertData.payment_id
    ) {
      const { data: existingRows } = await supabase
        .from('journal_entries')
        .select('*')
        .eq('reference_type', 'purchase')
        .eq('reference_id', entry.reference_id)
        .is('payment_id', null)
        .or('is_void.is.null,is_void.eq.false')
        .order('created_at', { ascending: true })
        .limit(1);
      const existing = (existingRows as Record<string, unknown>[] | null)?.[0] as
        | { id: string; is_void?: boolean }
        | undefined;
      if (existing?.id && existing.is_void !== true) {
        if (import.meta.env?.DEV) {
          console.warn('[accountingService] Duplicate canonical purchase JE insert — returning existing row:', existing.id);
        }
        return { ...existing, lines: [] };
      }
    }
    if (dup && entry.reference_type === 'correction_reversal' && insertData.reference_id) {
      const { data: existingRows } = await supabase
        .from('journal_entries')
        .select('*')
        .eq('company_id', entry.company_id)
        .eq('reference_type', 'correction_reversal')
        .eq('reference_id', insertData.reference_id)
        .or('is_void.is.null,is_void.eq.false')
        .order('created_at', { ascending: true })
        .limit(1);
      const existing = (existingRows as Record<string, unknown>[] | null)?.[0] as
        | { id: string; is_void?: boolean }
        | undefined;
      if (existing?.id && existing.is_void !== true) {
        if (import.meta.env?.DEV) {
          console.warn('[accountingService] Duplicate correction_reversal — returning existing row:', existing.id);
        }
        return { ...existing, lines: [] };
      }
    }
    if (dup && insertData.action_fingerprint) {
      const { data: fpRow } = await supabase
        .from('journal_entries')
        .select('*')
        .eq('company_id', entry.company_id)
        .eq('action_fingerprint', insertData.action_fingerprint)
        .limit(1)
        .maybeSingle();
      if (fpRow?.id && (fpRow as { is_void?: boolean }).is_void !== true) {
        if (import.meta.env?.DEV) {
          console.warn('[accountingService] Duplicate fingerprint JE — returning existing:', fpRow.id);
        }
        return { ...fpRow, lines: [] };
      }
    }

    if (entryError) throw entryError;

    // Insert journal entry lines
    const linesData = lines.map(line => ({
      journal_entry_id: entryData.id,
      account_id: line.account_id,
      debit: line.debit || 0,
      credit: line.credit || 0,
      description: line.description,
    }));

    const { error: linesError } = await supabase
      .from('journal_entry_lines')
      .insert(linesData);

    if (linesError) {
      const nowIso = new Date().toISOString();
      await supabase
        .from('journal_entries')
        .update({
          is_void: true,
          void_reason: 'posting_failed_rollback_no_lines',
          voided_at: nowIso,
        })
        .eq('id', entryData.id)
        .eq('company_id', entry.company_id);
      throw linesError;
    }

    if (linesData.length < 2 || totalDebit <= 0 || totalCredit <= 0) {
      const nowIso = new Date().toISOString();
      await supabase
        .from('journal_entries')
        .update({
          is_void: true,
          void_reason: 'posting_failed_rollback_invalid_lines',
          voided_at: nowIso,
        })
        .eq('id', entryData.id)
        .eq('company_id', entry.company_id);
      throw new Error('Journal entry must have at least two posted lines with non-zero totals.');
    }

    // Header totals: DB trigger (migration 20260434) keeps these in sync; set explicitly so older DBs and
    // immediate reads after insert are correct.
    const { error: totalsErr } = await supabase
      .from('journal_entries')
      .update({
        total_debit: totalDebit,
        total_credit: totalCredit,
      })
      .eq('id', entryData.id)
      .eq('company_id', entry.company_id);
    if (totalsErr) {
      console.warn('[ACCOUNTING] journal_entries total_debit/total_credit update failed:', totalsErr.message);
    }

    try {
      const { notifyAccountingEntriesChanged } = await import('@/app/lib/accountingInvalidate');
      notifyAccountingEntriesChanged({
        companyId: entry.company_id,
        entityId: entryData.id,
        reason: 'accounting-entries-changed',
      });
    } catch {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('accountingEntriesChanged', {
            detail: { entityId: entryData.id, journalEntryId: entryData.id },
          }),
        );
      }
    }

    // Return entry with lines
    return {
      ...entryData,
      lines: linesData.map((l, idx) => ({
        ...l,
        id: `temp-${idx}`,
      })),
    };
  },

  /**
   * Expense: update latest active expense JE (+ lines) and latest expense payment date — no reversal.
   * Use for HEADER_ONLY edits (same-month date move, description-only) after document row is updated.
   */
  async patchExpensePostingPresentation(params: {
    companyId: string;
    expenseId: string;
    entryDate?: string;
    journalDescription?: string;
  }): Promise<{ ok: boolean; error?: string }> {
    const { companyId, expenseId, entryDate, journalDescription } = params;
    if (!journalDescription && !entryDate) return { ok: true };

    const { data: jeRows, error: jeSelErr } = await supabase
      .from('journal_entries')
      .select('id')
      .eq('company_id', companyId)
      .eq('reference_type', 'expense')
      .eq('reference_id', expenseId)
      .or('is_void.is.null,is_void.eq.false')
      .order('created_at', { ascending: false })
      .limit(1);

    if (jeSelErr) return { ok: false, error: jeSelErr.message };
    const jeId = (jeRows as { id: string }[] | null)?.[0]?.id;
    if (!jeId) return { ok: false, error: 'No active expense journal for this document' };

    const jePatch: Record<string, unknown> = {};
    if (entryDate) jePatch.entry_date = String(entryDate).slice(0, 10);
    if (journalDescription !== undefined) jePatch.description = journalDescription;
    if (Object.keys(jePatch).length > 0) {
      const { error: jeUpdErr } = await supabase
        .from('journal_entries')
        .update(jePatch)
        .eq('id', jeId)
        .eq('company_id', companyId);
      if (jeUpdErr) return { ok: false, error: jeUpdErr.message };
    }

    if (journalDescription !== undefined) {
      const { error: lineErr } = await supabase
        .from('journal_entry_lines')
        .update({ description: journalDescription })
        .eq('journal_entry_id', jeId);
      if (lineErr) return { ok: false, error: lineErr.message };
    }

    const payDate = entryDate ? String(entryDate).slice(0, 10) : null;
    if (payDate) {
      const { data: payRows, error: paySelErr } = await supabase
        .from('payments')
        .select('id')
        .eq('company_id', companyId)
        .eq('reference_type', 'expense')
        .eq('reference_id', expenseId)
        .order('created_at', { ascending: false })
        .limit(1);
      if (paySelErr) return { ok: false, error: paySelErr.message };
      const payId = (payRows as { id: string }[] | null)?.[0]?.id;
      if (payId) {
        const { error: payUpdErr } = await supabase
          .from('payments')
          .update({ payment_date: payDate })
          .eq('id', payId)
          .eq('company_id', companyId);
        if (payUpdErr) return { ok: false, error: payUpdErr.message };
      }
    }

    return { ok: true };
  },

  /**
   * Pure manual journal (reference_type = journal or legacy manual): update header and/or replace lines.
   * Also allows transfer and orphan free-form types (e.g. legacy deposit) — same gate as Add Entry V2 open path.
   */
  async updateManualJournalEntry(
    companyId: string,
    journalEntryId: string,
    patch: {
      entry_date?: string;
      /** Posted-at timestamp (manual journals only) — does not change GL amounts. */
      created_at?: string;
      description?: string | null;
      lines?: { account_id: string; debit: number; credit: number; description?: string | null }[];
    }
  ): Promise<{ ok: boolean; error?: string }> {
    const { data: row, error: selErr } = await supabase
      .from('journal_entries')
      .select('id, reference_type, is_void, reference_id, payment_id')
      .eq('company_id', companyId)
      .eq('id', journalEntryId)
      .maybeSingle();

    if (selErr) return { ok: false, error: selErr.message };
    if (!row) return { ok: false, error: 'Journal not found' };
    if ((row as { is_void?: boolean }).is_void) return { ok: false, error: 'Void journal cannot be edited' };
    const rt = String((row as { reference_type?: string }).reference_type || '').toLowerCase();
    const referenceId = (row as { reference_id?: string | null }).reference_id;
    const paymentId = (row as { payment_id?: string | null }).payment_id;
    if (!canUpdateViaManualJournalEditor(rt, { referenceId, paymentId })) {
      return { ok: false, error: 'Only manual (journal/transfer) entries can be edited here; use Edit source for posted documents.' };
    }

    if (patch.entry_date !== undefined || patch.description !== undefined || patch.created_at !== undefined) {
      const header: Record<string, unknown> = {};
      if (patch.entry_date !== undefined) header.entry_date = String(patch.entry_date).slice(0, 10);
      if (patch.created_at !== undefined) header.created_at = patch.created_at;
      if (patch.description !== undefined) header.description = patch.description;
      const { error: up } = await supabase
        .from('journal_entries')
        .update(header)
        .eq('id', journalEntryId)
        .eq('company_id', companyId);
      if (up) return { ok: false, error: up.message };
    }

    if (patch.lines && patch.lines.length > 0) {
      const td = patch.lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
      const tc = patch.lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
      if (Math.abs(td - tc) > 0.02) return { ok: false, error: 'Journal lines are not balanced' };
      const { error: delErr } = await supabase.from('journal_entry_lines').delete().eq('journal_entry_id', journalEntryId);
      if (delErr) return { ok: false, error: delErr.message };
      const inserts = patch.lines.map((l) => ({
        journal_entry_id: journalEntryId,
        account_id: l.account_id,
        debit: Number(l.debit) || 0,
        credit: Number(l.credit) || 0,
        description: l.description ?? null,
      }));
      const { error: insErr } = await supabase.from('journal_entry_lines').insert(inserts);
      if (insErr) return { ok: false, error: insErr.message };
    }

    return { ok: true };
  },
};
